import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, collection, doc, setDoc, deleteDoc, getDocs, 
  onSnapshot, Firestore, query, where, orderBy, limit,
  DocumentData, QueryDocumentSnapshot, QuerySnapshot, 
  DocumentReference, CollectionReference,
  addDoc, updateDoc, serverTimestamp, Timestamp,
  getDoc, FieldValue, runTransaction,
  initializeFirestore, persistentLocalCache, 
  persistentSingleTabManager, CACHE_SIZE_UNLIMITED,
  enableIndexedDbPersistence, writeBatch, increment
} from 'firebase/firestore';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { redisService, CachedUserProgress, CachedUserSession, CachedUserAuth } from './redisService';

// Firebase configuration - use a valid project
const firebaseConfig = {
  apiKey: "AIzaSyBi8kVGUJ1Ssz4Yy9Edu4sDvvnXHAh5l1U",
  authDomain: "rawpa-b305e.firebaseapp.com",
  projectId: "rawpa-b305e",
  storageBucket: "rawpa-b305e.firebasestorage.app",
  messagingSenderId: "69216970500",
  appId: "1:69216970500:web:99f643155344f0e8a81251",
  measurementId: "G-BGZF7R1L0Q"
};

/**
 * Singleton Firebase implementation with error handling
 */
class FirebaseService {
  private static instance: FirebaseService;
  private app: FirebaseApp | null = null;
  private _auth: Auth | null = null;
  private _db: Firestore | null = null;
  private _storage: ReturnType<typeof getStorage> | null = null;
  private _isInitialized = false;
  private _error: Error | null = null;

  private constructor() {
    try {
      console.log("Initializing Firebase database...");
      
      // Add Firebase app options to disable analytics and other features causing IndexedDB errors
      const appOptions = {
        ...firebaseConfig,
        automaticDataCollectionEnabled: false
      };

      // Check if the app is already initialized
      if (getApps().length === 0) {
        this.app = initializeApp(appOptions);
      } else {
        this.app = getApp();
      }
      
      // Initialize services
      this._auth = getAuth(this.app);
      
      // Configure authentication persistence to ensure user sessions persist across devices
      setPersistence(this._auth, browserLocalPersistence)
        .then(() => {
          console.log("Firebase Auth persistence configured successfully");
        })
        .catch((error) => {
          console.error("Error setting Firebase Auth persistence:", error);
        });
      
      // Use regular getFirestore instead of initializeFirestore with persistence
      // This avoids IndexedDB errors in some browsers
      this._db = getFirestore(this.app);
      this._storage = getStorage(this.app);

      this._isInitialized = true;
      console.log("Firebase initialization complete");
    } catch (error) {
      console.error("Firebase initialization failed:", error);
      this._error = error as Error;
      this._isInitialized = false;
    }
  }

  public static getInstance(): FirebaseService {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  public get auth(): Auth {
    if (!this._auth) throw new Error('Firebase Auth is not initialized');
    return this._auth;
  }

  public get db(): Firestore {
    if (!this._db) throw new Error('Firestore is not initialized');
    return this._db;
  }

  public get storage(): ReturnType<typeof getStorage> {
    if (!this._storage) throw new Error('Firebase Storage is not initialized');
    return this._storage;
  }

  public get isInitialized(): boolean {
    return this._isInitialized;
  }

  public get error(): Error | null {
    return this._error;
  }
}

// Export singleton instances
const firebaseService = FirebaseService.getInstance();

export { firebaseService };
export const db = firebaseService.db;
export const auth = firebaseService.auth;
export const storage = firebaseService.storage;

// Collection names for Firestore
export const COLLECTIONS = {
  METHODOLOGIES: 'methodologies',
  USERS: 'users',
  USER_METHODOLOGIES: 'userMethodologies',
  SETTINGS: 'settings',
  GUIDED_METHODOLOGIES: 'guidedMethodologies',
  USER_PROGRESS: 'userProgress',
  SYSTEM: 'system',
  ADMIN_NOTES: 'adminNotes',
  HUNTING_TOOLKIT: 'huntingToolkit',
  CONTRIBUTIONS: 'contributions', // New
  CONTRIBUTORS: 'contributors',   // New
  HACKER_CHECKLISTS: 'hackerChecklists',
  USER_CHECKLIST_PROGRESS: 'userChecklistProgress',
  NEURAL_PATHWAY_SESSIONS: 'neuralPathwaySessions',
};

// Default methodologies for when Firebase is unavailable
export const DEFAULT_METHODOLOGIES: Methodology[] = [
  {
    id: "guided",
    title: "Guided Methodology",
    description: "Step by step guidance",
    subMethodologies: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: "straight-up",
    title: "Straight-up Methodology",
    description: "Direct approach",
    subMethodologies: [
      {
        id: "owasp",
        title: "OWASP Top 10",
        description: "Web application security risks",
        detailedContent: {
          name: "OWASP Top 10 (2021)",
          definition: "The OWASP Top 10 is a standard awareness document for developers and web application security. It represents a broad consensus about the most critical security risks to web applications.",
          keyPoints: [
            "A01:2021 - Broken Access Control",
            "A02:2021 - Cryptographic Failures",
            "A03:2021 - Injection",
            "A04:2021 - Insecure Design",
            "A05:2021 - Security Misconfiguration",
            "A06:2021 - Vulnerable and Outdated Components",
            "A07:2021 - Identification and Authentication Failures",
            "A08:2021 - Software and Data Integrity Failures",
            "A09:2021 - Security Logging and Monitoring Failures",
            "A10:2021 - Server-Side Request Forgery"
          ],
          commonBugs: [
            "Missing function-level access controls",
            "Weak password requirements and storage",
            "SQL, NoSQL, OS, or LDAP injection",
            "Lack of security requirements in design phase",
            "Verbose error messages exposing sensitive information",
            "Using components with known vulnerabilities"
          ],
          pentestingTips: [
            "Test access controls for escalation vulnerabilities",
            "Check for sensitive data transmitted in clear text",
            "Use automated scanners to identify injection points",
            "Review security requirements documentation",
            "Check for default settings, unnecessary features",
            "Verify third-party component versions"
          ],
          tools: [
            "OWASP ZAP",
            "Burp Suite",
            "SQLmap",
            "Nmap",
            "Dependency-Check",
            "OWASP Web Security Testing Guide"
          ],
          extra: "Remember that the OWASP Top 10 represents risks, not vulnerabilities. Each risk may contain multiple specific vulnerabilities that share the same root cause.",
          completedItems: []
        }
      }
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: "pathway",
    title: "Pathway Methodology",
    description: "Structured learning path",
    subMethodologies: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

export interface Methodology {
  id: string;
  title: string;
  description?: string;
  keywords?: string[];
  subMethodologies: SubMethodology[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SubMethodology {
  id: string;
  title: string;
  description?: string;
  keywords?: string[];
  detailedContent?: SubMethodologyContent;
  multiContent?: SubMethodologyMultiContent;
}

export interface ContentItem {
  id: string;
  text: string;
}

// Single content entry interface (used for backward compatibility)
export interface SubMethodologyContent {
  name?: string;
  definition?: string;
  keyPoints?: string[] | ContentItem[];
  commonBugs?: string[] | ContentItem[];
  pentestingTips?: string[] | ContentItem[];
  tools?: string[] | ContentItem[];
  extra?: string;
  completedItems?: string[];  // IDs or names of completed items for tracking progress
}

// For future implementation - will store multiple content entries per sub-methodology
export interface ContentEntry extends SubMethodologyContent {
  id: string;  // Unique ID for this content entry
}

// Future extension - multi-entry support
export interface SubMethodologyMultiContent {
  entries?: ContentEntry[];
  activeEntryId?: string;  // Currently selected entry
}

// Guided Methodology interfaces
export interface GuidedMethodologyStep {
  id: string;
  title: string;
  description: string;
  toolSuggestions: string[];
  toolSuggestionUrls?: Record<string, string>; // Map of tool names to their URLs
  commandExamples: string[];
  guidanceNotes: string;
  requiresUserInput: boolean;
  inputFieldLabel?: string;
  inputDataType?: 'Text' | 'Number' | 'List';
  nextStepType: 'Linear' | 'Conditional';
  nextStepId?: string;
  conditions?: StepCondition[];
}

export interface StepCondition {
  id: string;
  condition: string;
  value: string;
  nextStepId: string;
}

export interface GuidedMethodology {
  id: string;
  name: string;
  description: string;
  status: 'Active' | 'Inactive' | 'Draft';
  steps: GuidedMethodologyStep[];
  keywords?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface FirestoreTimestamps {
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
}

// Utility to ensure consistent timestamp handling
export const normalizeTimestamp = (timestamp: any): string => {
  if (!timestamp) return new Date().toISOString();
  if (typeof timestamp === 'string') return timestamp;
  if (timestamp && typeof timestamp.toDate === 'function') {
    try {
      return timestamp.toDate().toISOString();
    } catch (e) {
      console.error('Error converting Firestore timestamp:', e);
      return new Date().toISOString();
    }
  }
  if (timestamp instanceof Date) return timestamp.toISOString();
  return new Date().toISOString();
};

// For Firestore operations to handle server timestamps
export interface MethodologyWithTimestamps extends Omit<Methodology, 'createdAt' | 'updatedAt'> {
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
}

// Convert Firestore document to a Methodology
export const convertToMethodology = (doc: QueryDocumentSnapshot<DocumentData>): Methodology => {
  const data = doc.data();
  
  // Ensure subMethodologies exists and is an array
  let subMethodologies: SubMethodology[] = [];
  if (data.subMethodologies && Array.isArray(data.subMethodologies)) {
    // Validate each sub-methodology
    subMethodologies = data.subMethodologies.map(sm => ({
      id: sm.id || `generated-${Date.now()}`,
      title: sm.title || 'Untitled',
      description: sm.description || '',
      detailedContent: sm.detailedContent || {}, // Ensure we get the detailedContent
      multiContent: sm.multiContent || {} // Preserve multiContent field
    }));
  }
  
  // Create the methodology with all fields
  return {
    id: doc.id,
    title: data.title || '',
    description: data.description || '',
    subMethodologies,
    createdAt: normalizeTimestamp(data.createdAt),
    updatedAt: normalizeTimestamp(data.updatedAt)
  };
};

export interface LastSession {
  methodologyId: string;
  subMethodologyId?: string;
  lastAccessed: string;
  title: string;
  type: 'straight-up' | 'guided';
  completedSteps?: string[];
  lastStepIndex?: number;
}

export interface SystemSettings {
  disableLogin: boolean;
  disableSignup: boolean;
  disableAI: boolean;
  disablePentestOrchestrator: boolean;
  disableStraightUpMethodologies: boolean;
  disableGuidedMethodologies: boolean;
  disablePathway: boolean;
  disableHuntersBoard: boolean;
  disableHackerChecklists: boolean;
  disableLOLBAS: boolean;
  lolbasDescription: string;
  disableGTFOBins: boolean;
  gtfobinsDescription: string;
  disableWADComs: boolean;
  wadcomsDescription: string;
  disableReverseShell: boolean;
  reverseShellDescription: string;
  disableOSINT: boolean;
  osintDescription: string;
  featuredMethodology?: {
    id: string;
    title: string;
    type: 'straight-up' | 'guided' | 'pathway';
    description?: string;
  };
  featuredToolkit?: {
    id: string;
    title: string;
    type: 'lolbas' | 'gtfobins' | 'wadcoms' | 'reverse-shell' | 'osint' | 'hunting-toolkit';
    description?: string;
  };
}

export const methodologyService = {
  // Initialize default data
  initializeDefaultData: async (): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, using default methodologies");
      return false;
    }

    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.METHODOLOGIES));
      if (snapshot.empty) {
        for (const methodology of DEFAULT_METHODOLOGIES) {
          // Add server timestamps
          const methodologyWithTimestamps: MethodologyWithTimestamps = {
            id: methodology.id,
            title: methodology.title,
            description: methodology.description,
            subMethodologies: methodology.subMethodologies,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          await setDoc(doc(db, COLLECTIONS.METHODOLOGIES, methodology.id), methodologyWithTimestamps);
        }
      }
      return true;
    } catch (error) {
      console.error("Failed to initialize default methodologies:", error);
      return false;
    }
  },

  // Get all methodologies
  getMethodologies: async (): Promise<Methodology[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning default methodologies");
      return DEFAULT_METHODOLOGIES;
    }

    try {
      const methodologiesQuery = query(
        collection(db, COLLECTIONS.METHODOLOGIES),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(methodologiesQuery);
      return snapshot.docs.map(convertToMethodology);
    } catch (error) {
      console.error("Failed to get methodologies:", error);
      return DEFAULT_METHODOLOGIES;
    }
  },

  // Get a single methodology by ID
  getMethodologyById: async (methodologyId: string): Promise<Methodology | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, searching in default methodologies");
      const defaultMethodology = DEFAULT_METHODOLOGIES.find(m => m.id === methodologyId);
      return defaultMethodology || null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return convertToMethodology(docSnap as QueryDocumentSnapshot<DocumentData>);
      } else {
        console.warn(`Methodology with ID ${methodologyId} not found`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to get methodology with ID ${methodologyId}:`, error);
      return null;
    }
  },

  // Subscribe to methodology changes
  subscribeToMethodologies: (callback: (methodologies: Methodology[]) => void) => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, using default methodologies");
      callback(DEFAULT_METHODOLOGIES);
      return () => {}; // Return empty unsubscribe function
    }

    try {
      console.log("Setting up real-time listeners for methodologies and neural-pathways...");
      
      // Listen to both methodologies and neural-pathways collections
      const methodologiesQuery = query(
        collection(db, COLLECTIONS.METHODOLOGIES),
        orderBy('updatedAt', 'desc')
      );
      
      const neuralPathwaysQuery = query(
        collection(db, 'neural-pathways'),
        orderBy('updatedAt', 'desc')
      );
      
      // Create listeners for both collections
      const methodologiesUnsubscribe = onSnapshot(
        methodologiesQuery, 
        (methodologiesSnapshot) => {
          console.log(`Received update with ${methodologiesSnapshot.size} methodologies`);
          
          const neuralPathwaysUnsubscribe = onSnapshot(
            neuralPathwaysQuery,
            (neuralPathwaysSnapshot) => {
              console.log(`Received update with ${neuralPathwaysSnapshot.size} neural-pathways`);
              
              try {
                // Process methodologies collection
                const methodologies = methodologiesSnapshot.docs.map(doc => {
                  try {
                    const data = doc.data();
                    if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
                      data.subMethodologies = [];
                    }
                    return convertToMethodology(doc);
                  } catch (conversionError) {
                    console.error(`Error converting methodology document ${doc.id}:`, conversionError);
                    return {
                      id: doc.id,
                      title: doc.data().title || 'Error: Could not load title',
                      description: 'Error converting data',
                      subMethodologies: [],
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    };
                  }
                });
                
                // Process neural-pathways collection (convert to methodology format)
                const pathwayMethodologies = neuralPathwaysSnapshot.docs.map(doc => {
                  try {
                    const data = doc.data();
                    // Convert neural-pathway document to methodology format
                    return {
                      id: doc.id,
                      title: data.title || data.name || 'Pathway Methodology',
                      description: data.description || 'AI-generated pathway',
                      subMethodologies: [],
                      createdAt: normalizeTimestamp(data.createdAt),
                      updatedAt: normalizeTimestamp(data.updatedAt)
                    };
                  } catch (conversionError) {
                    console.error(`Error converting neural-pathway document ${doc.id}:`, conversionError);
                    return {
                      id: doc.id,
                      title: 'Pathway Methodology',
                      description: 'AI-generated pathway',
                      subMethodologies: [],
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    };
                  }
                });
                
                // Merge both collections
                const allMethodologies = [...methodologies, ...pathwayMethodologies];
                
                // Only log if there are sub-methodologies to reduce console noise
                const totalSubMethodologies = allMethodologies.reduce((sum, m) => sum + (m.subMethodologies?.length || 0), 0);
                if (totalSubMethodologies > 0) {
                  console.log(`Updating methodologies in UI: ${allMethodologies.length} methodologies with ${totalSubMethodologies} total sub-methodologies`);
                } else {
                  console.log(`Updating methodologies in UI: ${allMethodologies.length} methodologies`);
                }
                
                callback(allMethodologies);
              } catch (processingError) {
                console.error("Error processing methodologies update:", processingError);
                callback(DEFAULT_METHODOLOGIES);
              }
            },
            (error) => {
              console.error("Error in neural-pathways listener:", error);
              // If neural-pathways fails, still return methodologies
              const methodologies = methodologiesSnapshot.docs.map(doc => {
                try {
                  const data = doc.data();
                  if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
                    data.subMethodologies = [];
                  }
                  return convertToMethodology(doc);
                } catch (conversionError) {
                  console.error(`Error converting methodology document ${doc.id}:`, conversionError);
                  return {
                    id: doc.id,
                    title: doc.data().title || 'Error: Could not load title',
                    description: 'Error converting data',
                    subMethodologies: [],
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  };
                }
              });
              callback(methodologies);
            }
          );
          
          // Return cleanup function that unsubscribes from both listeners
          return () => {
            methodologiesUnsubscribe();
            neuralPathwaysUnsubscribe();
          };
        },
        (error) => {
          console.error("Error in methodologies listener:", error);
          callback(DEFAULT_METHODOLOGIES);
        }
      );
      
      // Return the outer unsubscribe function
      return methodologiesUnsubscribe;
    } catch (error) {
      console.error("Failed to subscribe to methodologies:", error);
      callback(DEFAULT_METHODOLOGIES);
      return () => {}; // Return empty unsubscribe function
    }
  },

  // Subscribe to a single methodology
  subscribeToMethodology: (methodologyId: string, callback: (methodology: Methodology | null) => void) => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, using default methodologies");
      const defaultMethodology = DEFAULT_METHODOLOGIES.find(m => m.id === methodologyId);
      callback(defaultMethodology || null);
      return () => {}; // Return empty unsubscribe function
    }

    try {
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      
      return onSnapshot(
        docRef, 
        (docSnap) => {
          if (docSnap.exists()) {
            callback(convertToMethodology(docSnap as QueryDocumentSnapshot<DocumentData>));
          } else {
            callback(null);
          }
        },
        (error) => {
          console.error(`Error subscribing to methodology ${methodologyId}:`, error);
          const defaultMethodology = DEFAULT_METHODOLOGIES.find(m => m.id === methodologyId);
          callback(defaultMethodology || null);
        }
      );
    } catch (error) {
      console.error(`Failed to subscribe to methodology ${methodologyId}:`, error);
      const defaultMethodology = DEFAULT_METHODOLOGIES.find(m => m.id === methodologyId);
      callback(defaultMethodology || null);
      return () => {}; // Return empty unsubscribe function
    }
  },

  // Save or update a methodology
  saveMethodology: async (methodology: Methodology): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot save methodology");
      return false;
    }

    try {
      // Extract the properties we need
      const { id, title, description, subMethodologies } = methodology;
      
      // Create object with the correct types for Firestore
      const methodologyData: Omit<MethodologyWithTimestamps, 'id'> = {
        title,
        description,
        subMethodologies,
        updatedAt: serverTimestamp()
      };
      
      // If createdAt doesn't exist, add it too
      if (!methodology.createdAt) {
        methodologyData.createdAt = serverTimestamp();
      }
      
      // Save with explicit ID reference
      await setDoc(doc(db, COLLECTIONS.METHODOLOGIES, id), methodologyData);
      console.log(`Methodology ${id} saved successfully`);
      return true;
    } catch (error) {
      console.error("Failed to save methodology:", error);
      return false;
    }
  },

  // Create a new methodology
  createMethodology: async (methodology: Omit<Methodology, 'id'>): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot create methodology");
      return null;
    }

    try {
      // Extract the properties we need
      const { title, description, subMethodologies = [] } = methodology;
      
      // Add server timestamps
      const methodologyData: Omit<MethodologyWithTimestamps, 'id'> = {
        title,
        description,
        subMethodologies,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.METHODOLOGIES), methodologyData);
      console.log(`New methodology created with ID: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error("Failed to create methodology:", error);
      return null;
    }
  },

  // Update a sub-methodology
  updateSubMethodology: async (
    methodologyId: string, 
    subMethodologyId: string, 
    updates: Partial<SubMethodology>
  ): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot update sub-methodology");
      return false;
    }

    try {
      // Define references
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      
      // Perform the update in a transaction to ensure consistency
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Methodology with ID ${methodologyId} not found`);
        }
        
        // Convert to methodology object
        const methodology = convertToMethodology(docSnap as QueryDocumentSnapshot<DocumentData>);
        
        // Find and update the sub-methodology
        const updatedSubMethodologies = methodology.subMethodologies.map(sm => 
          sm.id === subMethodologyId ? { ...sm, ...updates } : sm
        );
        
        console.log(`Updating sub-methodologies for ${methodologyId} via transaction:`, updatedSubMethodologies);
        
        // Update the document
        transaction.update(docRef, {
          subMethodologies: updatedSubMethodologies,
          updatedAt: serverTimestamp()
        });
      });
      
      console.log(`Sub-methodology ${subMethodologyId} updated successfully in methodology ${methodologyId} via transaction`);
      return true;
    } catch (error) {
      console.error("Failed to update sub-methodology:", error);
      return false;
    }
  },

  // Delete a methodology
  deleteMethodology: async (methodologyId: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot delete methodology");
      return false;
    }

    try {
      await deleteDoc(doc(db, COLLECTIONS.METHODOLOGIES, methodologyId));
      console.log(`Methodology ${methodologyId} deleted successfully`);
      return true;
    } catch (error) {
      console.error("Failed to delete methodology:", error);
      return false;
    }
  },

  // Update sub-methodology content
  updateSubMethodologyContent: async (
    methodologyId: string,
    subMethodologyId: string,
    content: SubMethodologyContent
  ): Promise<boolean> => {
    try {
      if (!firebaseService.isInitialized || !db) {
        console.warn("Firebase not initialized, cannot update sub-methodology content");
        return false;
      }

      // Define references
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      
      // Perform the update in a transaction to ensure consistency
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Methodology with ID ${methodologyId} not found`);
        }
        
        // Convert to methodology object
        const methodology = convertToMethodology(docSnap as QueryDocumentSnapshot<DocumentData>);
        
        // Find and update the sub-methodology with the detailed content
        const updatedSubMethodologies = methodology.subMethodologies.map(sm => 
          sm.id === subMethodologyId 
            ? { ...sm, detailedContent: content } 
            : sm
        );
        
        console.log(`Updating sub-methodology ${subMethodologyId} content via transaction`);
        
        // Update the document
        transaction.update(docRef, {
          subMethodologies: updatedSubMethodologies,
          updatedAt: serverTimestamp()
        });
      });
      
      console.log(`Sub-methodology ${subMethodologyId} content updated successfully`);
      return true;
    } catch (error) {
      console.error("Failed to update sub-methodology content:", error);
      return false;
    }
  },

  // Update sub-methodology with multiple content entries
  // This is a future function that will replace updateSubMethodologyContent when frontend is ready
  updateSubMethodologyMultiContent: async (
    methodologyId: string,
    subMethodologyId: string,
    entries: ContentEntry[]
  ): Promise<boolean> => {
    try {
      if (!firebaseService.isInitialized || !db) {
        console.warn("Firebase not initialized, cannot update sub-methodology content entries");
        return false;
      }

      console.log(`updateSubMethodologyMultiContent called for methodology=${methodologyId}, sub=${subMethodologyId}, entries=${entries.length}`);

      // Define references
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      
      // Perform the update in a transaction to ensure consistency
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Methodology with ID ${methodologyId} not found`);
        }
        
        // Convert to methodology object
        const methodology = convertToMethodology(docSnap as QueryDocumentSnapshot<DocumentData>);
        console.log("Current methodology data:", methodology.title, "Sub-methodologies count:", methodology.subMethodologies.length);
        
        // For now, just use the first entry as the main content for backward compatibility
        const mainContent = entries.length > 0 ? entries[0] : {} as ContentEntry;
        console.log("Using main content:", mainContent.name || 'Unnamed content');
        
        // Find and update the sub-methodology
        const foundIndex = methodology.subMethodologies.findIndex(sm => sm.id === subMethodologyId);
        if (foundIndex === -1) {
          throw new Error(`Sub-methodology with ID ${subMethodologyId} not found in methodology ${methodologyId}`);
        }
        
        const updatedSubMethodologies = [...methodology.subMethodologies];
        updatedSubMethodologies[foundIndex] = {
          ...updatedSubMethodologies[foundIndex],
          // Update both detailedContent and multiContent to ensure consistency
          // across components that access data in different ways
          detailedContent: mainContent,
          // Add multiContent field with all entries
          multiContent: { 
            entries, 
            activeEntryId: entries.length > 0 ? entries[0].id : undefined 
          }
        };
        
        console.log(`Updating sub-methodology ${subMethodologyId} with ${entries.length} content entries`);
        
        // Update the document with updatedAt timestamp
        const updateData = {
          subMethodologies: updatedSubMethodologies,
          updatedAt: serverTimestamp()
        };
        
        transaction.update(docRef, updateData);
        console.log("Transaction update data prepared");
      });
      
      console.log(`Sub-methodology ${subMethodologyId} multi-content updated successfully`);
      return true;
    } catch (error) {
      console.error("Failed to update sub-methodology multi-content:", error);
      return false;
    }
  },

  // Update completion status for sub-methodology items
  updateSubMethodologyCompletion: async (
    methodologyId: string,
    subMethodologyId: string,
    completedItems: string[]
  ): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot update completion status");
      return false;
    }

    try {
      // Define references
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      
      // Perform the update in a transaction
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Methodology with ID ${methodologyId} not found`);
        }
        
        // Get the raw data to preserve all fields
        const data = docSnap.data();
        if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
          throw new Error(`No sub-methodologies found in methodology ${methodologyId}`);
        }
        
        // Find the sub-methodology index
        const subMethodologyIndex = data.subMethodologies.findIndex(
          (sm: any) => sm.id === subMethodologyId
        );
        
        if (subMethodologyIndex === -1) {
          throw new Error(`Sub-methodology ${subMethodologyId} not found in methodology ${methodologyId}`);
        }
        
        // Create a copy of the sub-methodologies array
        const updatedSubMethodologies = [...data.subMethodologies];
        
        // Get the current sub-methodology
        const currentSubMethodology = updatedSubMethodologies[subMethodologyIndex];
        
        // Update the detailedContent with the new completedItems
        const detailedContent = currentSubMethodology.detailedContent || {};
        updatedSubMethodologies[subMethodologyIndex] = {
          ...currentSubMethodology,
          detailedContent: {
            ...detailedContent,
            completedItems
          }
        };
        
        // Always update the timestamp when completion status changes
        // This helps track the most recent session activity
        const updateData = {
          subMethodologies: updatedSubMethodologies,
          updatedAt: serverTimestamp()
        };
        
        // Update the document
        transaction.update(docRef, updateData);
        
        console.log(`Completion status updated for sub-methodology ${subMethodologyId} with ${completedItems.length} completed items`);
      });
      
      return true;
    } catch (error) {
      console.error("Failed to update completion status:", error);
      return false;
    }
  },

  // Direct method to get a sub-methodology's content
  getSubMethodologyContent: async (
    methodologyId: string,
    subMethodologyId: string
  ): Promise<SubMethodologyContent | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot get sub-methodology content");
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.warn(`Methodology with ID ${methodologyId} not found`);
        return null;
      }
      
      const data = docSnap.data();
      if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
        console.warn(`No sub-methodologies found in methodology ${methodologyId}`);
        return null;
      }
      
      const subMethodology = data.subMethodologies.find((sm: any) => sm.id === subMethodologyId);
      if (!subMethodology) {
        console.warn(`Sub-methodology ${subMethodologyId} not found in methodology ${methodologyId}`);
        return null;
      }
      
      // Check if we have multiContent entries - if so, use the first one
      if (subMethodology.multiContent && 
          subMethodology.multiContent.entries && 
          subMethodology.multiContent.entries.length > 0) {
        console.log(`Found ${subMethodology.multiContent.entries.length} entries in multiContent for ${subMethodologyId}`);
        
        // Get the active entry or default to the first one
        const activeEntryId = subMethodology.multiContent.activeEntryId;
        const activeEntry = activeEntryId 
          ? subMethodology.multiContent.entries.find((entry: any) => entry.id === activeEntryId)
          : subMethodology.multiContent.entries[0];
        
        if (activeEntry) {
          console.log(`Using active entry: ${activeEntry.name || 'Unnamed'}`);
          return activeEntry as SubMethodologyContent;
        }
      }
      
      return subMethodology.detailedContent || null;
    } catch (error) {
      console.error(`Failed to get sub-methodology ${subMethodologyId} content:`, error);
      return null;
    }
  },

  // Direct method to set a sub-methodology's content
  setSubMethodologyContent: async (
    methodologyId: string,
    subMethodologyId: string,
    content: SubMethodologyContent
  ): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot set sub-methodology content");
      return false;
    }

    try {
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.warn(`Methodology with ID ${methodologyId} not found`);
        return false;
      }
      
      const data = docSnap.data();
      if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
        console.warn(`No sub-methodologies found in methodology ${methodologyId}`);
        return false;
      }
      
      const subMethodologyIndex = data.subMethodologies.findIndex((sm: any) => sm.id === subMethodologyId);
      if (subMethodologyIndex === -1) {
        console.warn(`Sub-methodology ${subMethodologyId} not found in methodology ${methodologyId}`);
        return false;
      }
      
      // Create a copy of the sub-methodologies array
      const updatedSubMethodologies = [...data.subMethodologies];
      
      // Update the detailedContent of the specified sub-methodology
      updatedSubMethodologies[subMethodologyIndex] = {
        ...updatedSubMethodologies[subMethodologyIndex],
        detailedContent: content
      };
      
      // Update the document
      await updateDoc(docRef, {
        subMethodologies: updatedSubMethodologies,
        updatedAt: serverTimestamp()
      });
      
      console.log(`Sub-methodology ${subMethodologyId} content updated directly`);
      return true;
    } catch (error) {
      console.error(`Failed to set sub-methodology ${subMethodologyId} content:`, error);
      return false;
    }
  },

  // Get all content entries for a sub-methodology
  getAllSubMethodologyEntries: async (
    methodologyId: string,
    subMethodologyId: string
  ): Promise<ContentEntry[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot get sub-methodology entries");
      return [];
    }

    try {
      const docRef = doc(db, COLLECTIONS.METHODOLOGIES, methodologyId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        console.warn(`Methodology with ID ${methodologyId} not found`);
        return [];
      }
      
      const data = docSnap.data();
      if (!data.subMethodologies || !Array.isArray(data.subMethodologies)) {
        console.warn(`No sub-methodologies found in methodology ${methodologyId}`);
        return [];
      }
      
      const subMethodology = data.subMethodologies.find((sm: any) => sm.id === subMethodologyId);
      if (!subMethodology) {
        console.warn(`Sub-methodology ${subMethodologyId} not found in methodology ${methodologyId}`);
        return [];
      }
      
      const entries: ContentEntry[] = [];
      
      // Check for entries in multiContent field
      if (subMethodology.multiContent && 
          subMethodology.multiContent.entries && 
          Array.isArray(subMethodology.multiContent.entries) &&
          subMethodology.multiContent.entries.length > 0) {
        console.log(`Found ${subMethodology.multiContent.entries.length} entries in multiContent for ${subMethodologyId}`);
        return subMethodology.multiContent.entries as ContentEntry[];
      }
      
      // Fall back to detailedContent as a single entry if available
      if (subMethodology.detailedContent) {
        const { name, definition, keyPoints, commonBugs, pentestingTips, tools, extra, completedItems } = subMethodology.detailedContent;
        
        if (name || definition || keyPoints || commonBugs || pentestingTips || tools || extra) {
          entries.push({
            id: name ? name.replace(/\s+/g, '-').toLowerCase() : `entry-${Date.now()}`,
            name: name || subMethodology.title || 'Untitled Entry',
            definition: definition || '',
            keyPoints: keyPoints || [],
            commonBugs: commonBugs || [],
            pentestingTips: pentestingTips || [],
            tools: tools || [],
            extra: extra || '',
            completedItems: completedItems || []
          });
        }
      }
      
      return entries;
    } catch (error) {
      console.error(`Failed to get sub-methodology ${subMethodologyId} entries:`, error);
      return [];
    }
  },

  // Save user progress for session continuation
  saveUserProgress: async (
    methodologyId: string, 
    type: 'straight-up' | 'guided', 
    progressData: any
  ): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db || !auth || !auth.currentUser) {
      console.warn("Firebase not initialized or user not authenticated");
      return false;
    }

    try {
      const userId = auth.currentUser.uid;
      console.log(`Saving progress for user ${userId}, methodology ${methodologyId}, type ${type}`);
      
      const userProgressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      
      // Get existing progress data or create new
      const docSnap = await getDoc(userProgressRef);
      let userData = docSnap.exists() ? docSnap.data() : {};
      
      // Ensure we have a current timestamp for lastAccessed
      const now = new Date().toISOString();
      
      // Check if this is a new session with empty completed items
      const isNewSession = 
        (type === 'guided' && progressData.completedSteps && progressData.completedSteps.length === 0) ||
        (type === 'straight-up' && progressData.completedItems && progressData.completedItems.length === 0);
      
      if (isNewSession) {
        console.log(`Saving new session data for ${type} methodology ${methodologyId}`);
      }
      
      // Update with new progress data
      userData = {
        ...userData,
        [type === 'guided' ? 'guidedMethodology' : 'methodology']: {
          ...progressData,
          lastAccessed: now
        },
        updatedAt: serverTimestamp()
      };
      
      // Save to Firestore
      await setDoc(userProgressRef, userData, { merge: true });
      console.log(`User progress saved for ${type} methodology ${methodologyId} at ${now}`);
      
      // Cache in Redis/localStorage for cross-device persistence
      try {
        const deviceId = redisService.generateDeviceId();
        
        // Cache user progress
        const cachedProgress: CachedUserProgress = {
          userId,
          updatedAt: now,
          ...(type === 'guided' ? {
            guidedMethodology: {
              methodologyId,
              title: progressData.title || 'Guided Methodology',
              completedSteps: progressData.completedSteps || [],
              lastStepIndex: progressData.lastStepIndex || 0,
              lastAccessed: now
            }
          } : {
            methodology: {
              methodologyId,
              subMethodologyId: progressData.subMethodologyId,
              title: progressData.title || 'Methodology',
              completedItems: progressData.completedItems || [],
              lastAccessed: now
            }
          })
        };
        
        await redisService.cacheUserProgress(userId, cachedProgress);
        
        // Cache user session
        const cachedSession: CachedUserSession = {
          userId,
          deviceId,
          lastActive: now,
          methodologyId,
          subMethodologyId: progressData.subMethodologyId,
          type
        };
        
        await redisService.cacheUserSession(userId, cachedSession);
        
        console.log(`User progress cached for ${userId} (Redis: ${redisService.isAvailable()}, localStorage: ${!redisService.isAvailable()})`);
      } catch (error) {
        console.error('Error caching user progress:', error);
      }
      
      // If this is a straight-up methodology with a subMethodologyId and it's a new session,
      // also update the completedItems in the methodology document
      if (type === 'straight-up' && progressData.subMethodologyId && isNewSession) {
        try {
          console.log(`Resetting completedItems in methodology document for new session`);
          await methodologyService.updateSubMethodologyCompletion(
            methodologyId,
            progressData.subMethodologyId,
            [] // Empty array to clear all completed items
          );
        } catch (error) {
          console.error("Error updating methodology document for new session:", error);
        }
      }
      
      return true;
    } catch (error) {
      console.error("Failed to save user progress:", error);
      return false;
    }
  },

  // Ensure user progress is properly synchronized when authentication state changes
  ensureUserProgressSync: async (): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db || !auth || !auth.currentUser) {
      console.warn("Cannot sync user progress - Firebase not initialized or user not authenticated");
      return false;
    }

    try {
      const userId = auth.currentUser.uid;
      
      const userProgressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      const userProgressSnap = await getDoc(userProgressRef);
      
      if (userProgressSnap.exists()) {
        const userData = userProgressSnap.data();
        
        // Update the lastAccessed timestamp to ensure the session is marked as active
        await setDoc(userProgressRef, {
          ...userData,
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        // Sync with Redis/localStorage cache
        try {
          const deviceId = redisService.generateDeviceId();
          
          // Cache user auth info
          const cachedAuth: CachedUserAuth = {
            userId,
            email: auth.currentUser.email || '',
            displayName: auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'User',
            lastLogin: new Date().toISOString(),
            deviceId
          };
          
          await redisService.cacheUserAuth(userId, cachedAuth);
          
          // Sync progress with cache
          await redisService.syncUserProgress(userId, userData);
          
          console.log(`User progress synced (Redis: ${redisService.isAvailable()}, localStorage: ${!redisService.isAvailable()})`);
        } catch (error) {
          console.error('Error syncing with cache:', error);
        }
        
        return true;
      } else {
        // Initialize cache for new users
        try {
          const deviceId = redisService.generateDeviceId();
          
          const cachedAuth: CachedUserAuth = {
            userId,
            email: auth.currentUser.email || '',
            displayName: auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'User',
            lastLogin: new Date().toISOString(),
            deviceId
          };
          
          await redisService.cacheUserAuth(userId, cachedAuth);
          console.log(`Initialized cache for new user (Redis: ${redisService.isAvailable()}, localStorage: ${!redisService.isAvailable()})`);
        } catch (error) {
          console.error('Error initializing cache for new user:', error);
        }
        
        return true;
      }
    } catch (error) {
      console.error("Error ensuring user progress sync:", error);
      return false;
    }
  },

  // Find methodologies with completed items for the current user
  findMethodologiesWithCompletedItems: async (): Promise<LastSession | null> => {
    if (!firebaseService.isInitialized || !db || !auth || !auth.currentUser) {
      console.warn("Firebase not initialized or user not authenticated");
      return null;
    }

    try {
      const userId = auth.currentUser.uid;
      const startTime = Date.now();
      
      // Try methodology cache first for fastest retrieval
      try {
        const cachedMethodologyData = await redisService.getCachedMethodologyData(userId);
        if (cachedMethodologyData?.lastSession) {
          console.log(`Found cached methodology data for user ${userId} in ${Date.now() - startTime}ms`);
          return cachedMethodologyData.lastSession;
        }
      } catch (error) {
        console.error('Error retrieving methodology data from cache:', error);
      }
      
      // Try Redis/localStorage cache as fallback
      try {
        const cachedProgress = await redisService.getCachedUserProgress(userId);
        const cachedSession = await redisService.getCachedUserSession(userId);
        
        if (cachedProgress && cachedSession) {
          console.log(`Found cached progress for user ${userId} (Redis: ${redisService.isAvailable()}, localStorage: ${!redisService.isAvailable()})`);
          
          // Return the most recent session from cache
          if (cachedProgress.guidedMethodology && cachedProgress.methodology) {
            // Compare timestamps to return the most recent
            const guidedDate = new Date(cachedProgress.guidedMethodology.lastAccessed).getTime();
            const straightUpDate = new Date(cachedProgress.methodology.lastAccessed).getTime();
            
            if (guidedDate > straightUpDate) {
              const result = {
                methodologyId: cachedProgress.guidedMethodology.methodologyId,
                lastAccessed: cachedProgress.guidedMethodology.lastAccessed,
                title: cachedProgress.guidedMethodology.title,
                type: 'guided' as const,
                completedSteps: cachedProgress.guidedMethodology.completedSteps,
                lastStepIndex: cachedProgress.guidedMethodology.lastStepIndex
              };
              
              // Cache the result for future use
              await redisService.cacheMethodologyData(userId, {
                userId,
                lastSession: result,
                cachedAt: new Date().toISOString()
              });
              
              return result;
            } else {
              const result = {
                methodologyId: cachedProgress.methodology.methodologyId,
                subMethodologyId: cachedProgress.methodology.subMethodologyId,
                lastAccessed: cachedProgress.methodology.lastAccessed,
                title: cachedProgress.methodology.title,
                type: 'straight-up' as const
              };
              
              // Cache the result for future use
              await redisService.cacheMethodologyData(userId, {
                userId,
                lastSession: result,
                cachedAt: new Date().toISOString()
              });
              
              return result;
            }
          } else if (cachedProgress.guidedMethodology) {
            const result = {
              methodologyId: cachedProgress.guidedMethodology.methodologyId,
              lastAccessed: cachedProgress.guidedMethodology.lastAccessed,
              title: cachedProgress.guidedMethodology.title,
              type: 'guided' as const,
              completedSteps: cachedProgress.guidedMethodology.completedSteps,
              lastStepIndex: cachedProgress.guidedMethodology.lastStepIndex
            };
            
            // Cache the result for future use
            await redisService.cacheMethodologyData(userId, {
              userId,
              lastSession: result,
              cachedAt: new Date().toISOString()
            });
            
            return result;
          } else if (cachedProgress.methodology) {
            const result = {
              methodologyId: cachedProgress.methodology.methodologyId,
              subMethodologyId: cachedProgress.methodology.subMethodologyId,
              lastAccessed: cachedProgress.methodology.lastAccessed,
              title: cachedProgress.methodology.title,
              type: 'straight-up' as const
            };
            
            // Cache the result for future use
            await redisService.cacheMethodologyData(userId, {
              userId,
              lastSession: result,
              cachedAt: new Date().toISOString()
            });
            
            return result;
          }
        }
      } catch (error) {
        console.error('Error retrieving from cache:', error);
      }
      
      // Optimized Firestore query - batch operations
      console.log(`Cache miss, querying Firestore for user ${userId}`);
      const userProgressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      const userProgressSnap = await getDoc(userProgressRef);
      
      if (userProgressSnap.exists()) {
        const userData = userProgressSnap.data();
        
        // Store potential sessions to compare timestamps
        let guidedSession: LastSession | null = null;
        let straightUpSession: LastSession | null = null;
        
        // Batch methodology lookups for better performance
        const methodologyPromises: Promise<any>[] = [];
        
        // Check for guided methodology progress
        if (userData.guidedMethodology && userData.guidedMethodology.methodologyId) {
          methodologyPromises.push(
            methodologyService.getGuidedMethodologyById(userData.guidedMethodology.methodologyId)
              .then(guidedMethodology => {
                if (guidedMethodology) {
                  console.log(`Found guided methodology session: ${guidedMethodology.id}`);
                  guidedSession = {
                    methodologyId: guidedMethodology.id,
                    lastAccessed: userData.guidedMethodology.lastAccessed || new Date().toISOString(),
                    title: guidedMethodology.name,
                    type: 'guided',
                    completedSteps: userData.guidedMethodology.completedSteps || [],
                    lastStepIndex: userData.guidedMethodology.lastStepIndex || 0
                  };
                }
                return guidedSession;
              })
              .catch(error => {
                console.warn(`Error retrieving guided methodology ${userData.guidedMethodology.methodologyId}:`, error);
                return null;
              })
          );
        }
        
        // Check for straight-up methodology progress
        if (userData.methodology && userData.methodology.methodologyId) {
          methodologyPromises.push(
            methodologyService.getMethodologyById(userData.methodology.methodologyId)
              .then(methodology => {
                if (methodology) {
                  // If there's a subMethodologyId, verify it exists
                  if (userData.methodology.subMethodologyId) {
                    const subMethodologyExists = methodology.subMethodologies.some(
                      sm => sm.id === userData.methodology.subMethodologyId
                    );
                    
                    if (subMethodologyExists) {
                      console.log(`Found straight-up methodology session: ${userData.methodology.methodologyId}/${userData.methodology.subMethodologyId}`);
                      
                      straightUpSession = {
                        methodologyId: userData.methodology.methodologyId,
                        subMethodologyId: userData.methodology.subMethodologyId,
                        lastAccessed: userData.methodology.lastAccessed || new Date().toISOString(),
                        title: userData.methodology.title || 'Previous Session',
                        type: 'straight-up'
                      };
                    }
                  } else {
                    // If there's no subMethodologyId, just return the methodology
                    straightUpSession = {
                      methodologyId: userData.methodology.methodologyId,
                      lastAccessed: userData.methodology.lastAccessed || new Date().toISOString(),
                      title: userData.methodology.title || 'Previous Session',
                      type: 'straight-up'
                    };
                  }
                }
                return straightUpSession;
              })
              .catch(error => {
                console.warn(`Error retrieving methodology ${userData.methodology.methodologyId}:`, error);
                return null;
              })
          );
        }
        
        // Wait for all methodology lookups to complete
        await Promise.all(methodologyPromises);
        
        // Compare timestamps and return the most recent session
        if (guidedSession && straightUpSession) {
          console.log(`Comparing session timestamps - Guided: ${guidedSession.lastAccessed}, Straight-up: ${straightUpSession.lastAccessed}`);
          
          const guidedDate = new Date(guidedSession.lastAccessed).getTime();
          const straightUpDate = new Date(straightUpSession.lastAccessed).getTime();
          
          let result: LastSession;
          if (guidedDate > straightUpDate) {
            console.log("Guided methodology session is more recent");
            result = guidedSession;
          } else {
            console.log("Straight-up methodology session is more recent");
            result = straightUpSession;
          }
          
          // Cache the result for future use
          await redisService.cacheMethodologyData(userId, {
            userId,
            lastSession: result,
            cachedAt: new Date().toISOString()
          });
          
          console.log(`Total time to find session: ${Date.now() - startTime}ms`);
          return result;
        }
        
        // If only one type of session exists, return that one
        if (guidedSession) {
          await redisService.cacheMethodologyData(userId, {
            userId,
            lastSession: guidedSession,
            cachedAt: new Date().toISOString()
          });
          console.log(`Total time to find session: ${Date.now() - startTime}ms`);
          return guidedSession;
        }
        if (straightUpSession) {
          await redisService.cacheMethodologyData(userId, {
            userId,
            lastSession: straightUpSession,
            cachedAt: new Date().toISOString()
          });
          console.log(`Total time to find session: ${Date.now() - startTime}ms`);
          return straightUpSession;
        }
      }
      
      // Fall back to legacy method - check methodologies for completed items
      try {
      const methodologiesQuery = query(
        collection(db, COLLECTIONS.METHODOLOGIES),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(methodologiesQuery);
      if (snapshot.empty) {
        console.log("No methodologies found");
        return null;
      }
      
      // Find the first methodology with completed items in any sub-methodology
      for (const doc of snapshot.docs) {
        const methodology = convertToMethodology(doc as QueryDocumentSnapshot<DocumentData>);
        
        // Check each sub-methodology for completed items
        for (const subMethodology of methodology.subMethodologies) {
          if (subMethodology.detailedContent?.completedItems && 
              subMethodology.detailedContent.completedItems.length > 0) {
            
              console.log(`Found legacy session: ${methodology.id}/${subMethodology.id} with ${subMethodology.detailedContent.completedItems.length} completed items`);
            
            const result = {
              methodologyId: methodology.id,
              subMethodologyId: subMethodology.id,
              lastAccessed: methodology.updatedAt || new Date().toISOString(),
              title: subMethodology.title,
              type: 'straight-up' as const
            };
            
            // Cache the result for future use
            await redisService.cacheMethodologyData(userId, {
              userId,
              lastSession: result,
              cachedAt: new Date().toISOString()
            });
            
            console.log(`Total time to find session: ${Date.now() - startTime}ms`);
            return result;
          }
        }
        }
      } catch (error) {
        console.error("Error in legacy method for finding completed items:", error);
      }
      
      console.log("No methodologies with completed items found");
      console.log(`Total time to find session: ${Date.now() - startTime}ms`);
      return null;
    } catch (error) {
      console.error("Failed to find methodologies with completed items:", error);
      return null;
    }
  },

  // Guided Methodology Methods
  getGuidedMethodologies: async (): Promise<GuidedMethodology[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning empty guided methodologies");
      return [];
    }

    try {
      const guidedMethodologiesQuery = query(
        collection(db, COLLECTIONS.GUIDED_METHODOLOGIES),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(guidedMethodologiesQuery);
      const result = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          status: data.status || 'Draft',
          steps: data.steps || [],
          keywords: data.keywords || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      });
      if (result.length === 0) {
        console.warn('[DEBUG] No guided methodologies found in Firestore!');
      }
      return result;
    } catch (error) {
      console.error("Failed to get guided methodologies:", error);
      return [];
    }
  },

  getGuidedMethodologyById: async (id: string): Promise<GuidedMethodology | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot get guided methodology");
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.GUIDED_METHODOLOGIES, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name || '',
          description: data.description || '',
          status: data.status || 'Draft',
          steps: data.steps || [],
          keywords: data.keywords || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      } else {
        console.warn(`Guided methodology with ID ${id} not found`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to get guided methodology with ID ${id}:`, error);
      return null;
    }
  },

  createGuidedMethodology: async (methodology: Omit<GuidedMethodology, 'id'>): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot create guided methodology");
      return null;
    }

    try {
      const { name, description, status, steps, keywords } = methodology;
      
      const methodologyData = {
        name: name || '',
        description: description || '',
        status: status || 'Draft',
        steps: steps || [],
        keywords: keywords || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.GUIDED_METHODOLOGIES), methodologyData);
      return docRef.id;
    } catch (error) {
      console.error("Failed to create guided methodology:", error);
      return null;
    }
  },

  saveGuidedMethodology: async (methodology: GuidedMethodology): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot save guided methodology");
      return false;
    }

    try {
      const { id, name, description, status, steps, keywords } = methodology;
      
      // Deeply sanitize steps to ensure no undefined values
      const sanitizedSteps = steps.map(step => {
        // Handle basic step properties
        const sanitizedStep: GuidedMethodologyStep = {
          id: step.id,
          title: step.title || '',
          description: step.description || '',
          toolSuggestions: step.toolSuggestions || [],
          toolSuggestionUrls: step.toolSuggestionUrls || {},
          commandExamples: step.commandExamples || [],
          guidanceNotes: step.guidanceNotes || '',
          requiresUserInput: Boolean(step.requiresUserInput),
          inputFieldLabel: step.requiresUserInput ? (step.inputFieldLabel || 'Enter input') : '',
          inputDataType: step.requiresUserInput ? (step.inputDataType || 'Text') : 'Text',
          nextStepType: step.nextStepType || 'Linear',
        };
        
        // Handle specific logic for Linear vs Conditional flow
        if (step.nextStepType === 'Linear') {
          // For Linear flow, ensure nextStepId is properly set
          // Keep as undefined for type-safety, will be converted to null by JSON.stringify
          sanitizedStep.nextStepId = step.nextStepId || undefined;
          sanitizedStep.conditions = []; // Empty array for Linear flow
        } else {
          // For Conditional flow, nextStepId is not used
          // Keep as undefined for type-safety, will be converted to null by JSON.stringify
          sanitizedStep.nextStepId = undefined;
          
          // Sanitize each condition
          sanitizedStep.conditions = (step.conditions || []).map(condition => ({
            id: condition.id || `condition-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            condition: condition.condition || 'equals',
            value: condition.value || '',
            // Per StepCondition interface, nextStepId must be a string
            nextStepId: condition.nextStepId === 'end' || condition.nextStepId === null 
              ? 'end' 
              : (condition.nextStepId || 'end')
          }));
        }
        
        return sanitizedStep;
      });
      
      // Final replacement of any undefined values with nulls using JSON serialization
      const methodologyData = JSON.parse(JSON.stringify({
        name: name || '',
        description: description || '',
        status: status || 'Draft',
        steps: sanitizedSteps,
        keywords: keywords || [],
        updatedAt: serverTimestamp()
      }));
      
      // Log the sanitized data for debugging
      console.log(`Saving guided methodology ${id} with ${sanitizedSteps.length} sanitized steps`);
      
      await setDoc(doc(db, COLLECTIONS.GUIDED_METHODOLOGIES, id), methodologyData, { merge: true });
      return true;
    } catch (error) {
      console.error("Failed to save guided methodology:", error);
      return false;
    }
  },

  deleteGuidedMethodology: async (id: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot delete guided methodology");
      return false;
    }

    try {
      await deleteDoc(doc(db, COLLECTIONS.GUIDED_METHODOLOGIES, id));
      return true;
    } catch (error) {
      console.error(`Failed to delete guided methodology ${id}:`, error);
      return false;
    }
  },

  // Clean up invalid sessions that reference non-existent methodologies
  cleanupInvalidSessions: async (): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db || !auth || !auth.currentUser) {
      console.warn("Firebase not initialized or user not authenticated");
      return false;
    }

    try {
      const userId = auth.currentUser.uid;
      const userProgressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      const userProgressSnap = await getDoc(userProgressRef);
      
      if (!userProgressSnap.exists()) {
        return true; // No data to clean up
      }
      
      const userData = userProgressSnap.data();
      let needsUpdate = false;
      
      // Check guided methodology
      if (userData.guidedMethodology && userData.guidedMethodology.methodologyId) {
        try {
          const guidedMethodology = await methodologyService.getGuidedMethodologyById(
            userData.guidedMethodology.methodologyId
          );
          
          if (!guidedMethodology) {
            console.log(`Cleaning up invalid guided methodology reference: ${userData.guidedMethodology.methodologyId}`);
            delete userData.guidedMethodology;
            needsUpdate = true;
          }
        } catch (error) {
          console.warn(`Error checking guided methodology ${userData.guidedMethodology.methodologyId}, will remove reference:`, error);
          delete userData.guidedMethodology;
          needsUpdate = true;
        }
      }
      
      // Check straight-up methodology
      if (userData.methodology && userData.methodology.methodologyId) {
        try {
          const methodology = await methodologyService.getMethodologyById(
            userData.methodology.methodologyId
          );
          
          if (!methodology) {
            console.log(`Cleaning up invalid methodology reference: ${userData.methodology.methodologyId}`);
            delete userData.methodology;
            needsUpdate = true;
          } else if (userData.methodology.subMethodologyId) {
            // Check if sub-methodology exists
            const subMethodologyExists = methodology.subMethodologies.some(
              sm => sm.id === userData.methodology.subMethodologyId
            );
            
            if (!subMethodologyExists) {
              console.log(`Cleaning up invalid sub-methodology reference: ${userData.methodology.subMethodologyId}`);
              delete userData.methodology.subMethodologyId;
              needsUpdate = true;
            }
          }
        } catch (error) {
          console.warn(`Error checking methodology ${userData.methodology.methodologyId}, will remove reference:`, error);
          delete userData.methodology;
          needsUpdate = true;
        }
      }
      
      // Update user progress if needed
      if (needsUpdate) {
        await setDoc(userProgressRef, {
          ...userData,
          updatedAt: serverTimestamp()
        });
        console.log("User progress cleaned up successfully");
      }
      
      return true;
    } catch (error) {
      console.error("Failed to clean up invalid sessions:", error);
      return false;
    }
  },

  // Get total user count
  getTotalUserCount: async (): Promise<number> => {
    if (!firebaseService.isInitialized || !db) return 0;
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.USER_PROGRESS));
      return snapshot.size;
    } catch (error) {
      console.error('Failed to get total user count:', error);
      return 0;
    }
  },

  // Get active session count (users with updatedAt in USER_PROGRESS within last 24 hours)
  getActiveSessionCount: async (): Promise<number> => {
    if (!firebaseService.isInitialized || !db) return 0;
    try {
      const now = Date.now();
      const oneDayMs = 24 * 60 * 60 * 1000;
      const snapshot = await getDocs(collection(db, COLLECTIONS.USER_PROGRESS));
      let count = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        let updatedAt = data.updatedAt;
        if (updatedAt && typeof updatedAt.toDate === 'function') {
          updatedAt = updatedAt.toDate();
        } else if (typeof updatedAt === 'string') {
          updatedAt = new Date(updatedAt);
        }
        if (updatedAt && now - new Date(updatedAt).getTime() < oneDayMs) {
          count++;
        }
      });
      return count;
    } catch (error) {
      console.error('Failed to get active session count:', error);
      return 0;
    }
  },

  // Get detailed user statistics
  getUserStatistics: async (): Promise<{
    totalUsers: number;
    activeUsers: number;
    averageSessionsPerUser: number;
    usersByDate: Record<string, number>;
  }> => {
    if (!firebaseService.isInitialized || !db) {
      return {
        totalUsers: 0,
        activeUsers: 0,
        averageSessionsPerUser: 0,
        usersByDate: {}
      };
    }

    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.USER_PROGRESS));
      const now = Date.now();
      const oneDayMs = 24 * 60 * 60 * 1000;
      const oneWeekMs = 7 * oneDayMs;

      let totalUsers = snapshot.size;
      let activeUsers = 0;
      let totalSessions = 0;
      const usersByDate: Record<string, number> = {};

      // Initialize the last 7 days in usersByDate
      for (let i = 0; i < 7; i++) {
        const date = new Date(now - i * oneDayMs);
        const dateString = date.toISOString().split('T')[0];
        usersByDate[dateString] = 0;
      }

      snapshot.forEach(doc => {
        const data = doc.data();
        let updatedAt = data.updatedAt;
        
        if (updatedAt && typeof updatedAt.toDate === 'function') {
          updatedAt = updatedAt.toDate();
        } else if (typeof updatedAt === 'string') {
          updatedAt = new Date(updatedAt);
        }

        // Count active users (last 24 hours)
        if (updatedAt && now - new Date(updatedAt).getTime() < oneDayMs) {
          activeUsers++;
        }

        // Count by date for the last 7 days
        if (updatedAt && now - new Date(updatedAt).getTime() < oneWeekMs) {
          const dateString = new Date(updatedAt).toISOString().split('T')[0];
          usersByDate[dateString] = (usersByDate[dateString] || 0) + 1;
        }

        // Count total sessions
        let sessionCount = 0;
        if (data.guidedMethodology) sessionCount++;
        if (data.methodology) sessionCount++;
        totalSessions += sessionCount;
      });

      return {
        totalUsers,
        activeUsers,
        averageSessionsPerUser: totalUsers > 0 ? totalSessions / totalUsers : 0,
        usersByDate
      };
    } catch (error) {
      console.error('Failed to get user statistics:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        averageSessionsPerUser: 0,
        usersByDate: {}
      };
    }
  },

  // Get most used methodologies
  getMostUsedMethodologies: async (): Promise<{
    straight: { id: string; title: string; count: number }[];
    guided: { id: string; title: string; count: number }[];
  }> => {
    if (!firebaseService.isInitialized || !db) {
      return { straight: [], guided: [] };
    }

    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.USER_PROGRESS));
      
      // Count methodologies
      const straightCounts: Record<string, number> = {};
      const guidedCounts: Record<string, number> = {};
      const straightTitles: Record<string, string> = {};
      const guidedTitles: Record<string, string> = {};

      // Process each user's progress data
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        // Count straight-up methodologies
        if (data.methodology && data.methodology.methodologyId) {
          const id = data.methodology.methodologyId;
          straightCounts[id] = (straightCounts[id] || 0) + 1;
          if (data.methodology.title) {
            straightTitles[id] = data.methodology.title;
          }
        }
        
        // Count guided methodologies
        if (data.guidedMethodology && data.guidedMethodology.methodologyId) {
          const id = data.guidedMethodology.methodologyId;
          guidedCounts[id] = (guidedCounts[id] || 0) + 1;
          if (data.guidedMethodology.title) {
            guidedTitles[id] = data.guidedMethodology.title;
          }
        }
      }

      // Fetch missing titles for methodologies
      const straightIds = Object.keys(straightCounts);
      const guidedIds = Object.keys(guidedCounts);
      
      // Get missing straight-up methodology titles
      for (const id of straightIds) {
        if (!straightTitles[id]) {
          try {
            const methodology = await methodologyService.getMethodologyById(id);
            if (methodology) {
              straightTitles[id] = methodology.title;
            }
          } catch (error) {
            console.error(`Error fetching title for methodology ${id}:`, error);
          }
        }
      }
      
      // Get missing guided methodology titles
      for (const id of guidedIds) {
        if (!guidedTitles[id]) {
          try {
            const methodology = await methodologyService.getGuidedMethodologyById(id);
            if (methodology) {
              guidedTitles[id] = methodology.name;
            }
          } catch (error) {
            console.error(`Error fetching title for guided methodology ${id}:`, error);
          }
        }
      }

      // Convert to sorted arrays
      const straight = straightIds
        .map(id => ({
          id,
          title: straightTitles[id] || id,
          count: straightCounts[id]
        }))
        .sort((a, b) => b.count - a.count);
      
      const guided = guidedIds
        .map(id => ({
          id,
          title: guidedTitles[id] || id,
          count: guidedCounts[id]
        }))
        .sort((a, b) => b.count - a.count);

      return { straight, guided };
    } catch (error) {
      console.error('Failed to get most used methodologies:', error);
      return { straight: [], guided: [] };
    }
  },

  // Get all user sessions
  getAllUserSessions: async (): Promise<{
    userId: string;
    email?: string;
    displayName?: string;
    lastActive: string;
    methodologies: {
      type: 'straight-up' | 'guided';
      id: string;
      title: string;
      lastAccessed: string;
    }[];
  }[]> => {
    if (!firebaseService.isInitialized || !db) return [];

    try {
      const sessionsSnapshot = await getDocs(collection(db, COLLECTIONS.USER_PROGRESS));
      const userSessions = [];

      for (const doc of sessionsSnapshot.docs) {
        const userId = doc.id;
        const data = doc.data();
        let lastActive = new Date(0).toISOString();
        const methodologies = [];

        // Process straight-up methodology
        if (data.methodology) {
          const lastAccessed = data.methodology.lastAccessed || data.updatedAt || new Date().toISOString();
          
          // Update last active time if this is more recent
          if (new Date(lastAccessed) > new Date(lastActive)) {
            lastActive = lastAccessed;
          }
          
          methodologies.push({
            type: 'straight-up' as const,
            id: data.methodology.methodologyId,
            title: data.methodology.title || 'Unknown Methodology',
            lastAccessed
          });
        }

        // Process guided methodology
        if (data.guidedMethodology) {
          const lastAccessed = data.guidedMethodology.lastAccessed || data.updatedAt || new Date().toISOString();
          
          // Update last active time if this is more recent
          if (new Date(lastAccessed) > new Date(lastActive)) {
            lastActive = lastAccessed;
          }
          
          methodologies.push({
            type: 'guided' as const,
            id: data.guidedMethodology.methodologyId,
            title: data.guidedMethodology.title || 'Unknown Guided Methodology',
            lastAccessed
          });
        }

        // Get user details if available
        let email = undefined;
        let displayName = undefined;
        
        try {
          // This is just a placeholder - in a real app, you would use Firebase Admin SDK
          // to get user details by ID, but we can't do that from the client
          // Instead, we'll use any available info in the user progress document
          if (data.userEmail) {
            email = data.userEmail;
          }
          if (data.userDisplayName) {
            displayName = data.userDisplayName;
          }
        } catch (error) {
          console.error(`Error fetching details for user ${userId}:`, error);
        }

        userSessions.push({
          userId,
          email,
          displayName,
          lastActive,
          methodologies
        });
      }

      // Sort by last active time (most recent first)
      return userSessions.sort((a, b) => 
        new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
      );
    } catch (error) {
      console.error('Failed to get all user sessions:', error);
      return [];
    }
  },

  async getSystemSettings(): Promise<SystemSettings> {
    try {
      if (!db) throw new Error('Firestore is not initialized');
      
      const docRef = doc(db, COLLECTIONS.SYSTEM, 'settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          disableLogin: data.disableLogin || false,
          disableSignup: data.disableSignup || false,
          disableAI: data.disableAI || false,
          disablePentestOrchestrator: data.disablePentestOrchestrator || false,
          disableStraightUpMethodologies: data.disableStraightUpMethodologies || false,
          disableGuidedMethodologies: data.disableGuidedMethodologies || false,
          disablePathway: data.disablePathway || false,
          disableHuntersBoard: data.disableHuntersBoard || false,
          disableHackerChecklists: data.disableHackerChecklists || false,
          disableLOLBAS: data.disableLOLBAS || false,
          lolbasDescription: data.lolbasDescription || '',
          disableGTFOBins: data.disableGTFOBins || false,
          gtfobinsDescription: data.gtfobinsDescription || '',
          disableWADComs: data.disableWADComs || false,
          wadcomsDescription: data.wadcomsDescription || '',
          disableReverseShell: data.disableReverseShell || false,
          reverseShellDescription: data.reverseShellDescription || '',
          disableOSINT: data.disableOSINT || false,
          osintDescription: data.osintDescription || '',
          featuredMethodology: data.featuredMethodology || undefined,
          featuredToolkit: data.featuredToolkit || undefined
        };
      }
      
      // Default settings if document doesn't exist
      return {
        disableLogin: false,
        disableSignup: false,
        disableAI: false,
        disablePentestOrchestrator: false,
        disableStraightUpMethodologies: false,
        disableGuidedMethodologies: false,
        disablePathway: false,
        disableHuntersBoard: false,
        disableHackerChecklists: false,
        disableLOLBAS: false,
        lolbasDescription: '',
        disableGTFOBins: false,
        gtfobinsDescription: '',
        disableWADComs: false,
        wadcomsDescription: '',
        disableReverseShell: false,
        reverseShellDescription: '',
        disableOSINT: false,
        osintDescription: '',
        featuredMethodology: undefined,
        featuredToolkit: undefined
      };
    } catch (error) {
      console.error('Error getting system settings:', error);
      
      // Return default settings on error
      return {
        disableLogin: false,
        disableSignup: false,
        disableAI: false,
        disablePentestOrchestrator: false,
        disableStraightUpMethodologies: false,
        disableGuidedMethodologies: false,
        disablePathway: false,
        disableHuntersBoard: false,
        disableHackerChecklists: false,
        disableLOLBAS: false,
        lolbasDescription: '',
        disableGTFOBins: false,
        gtfobinsDescription: '',
        disableWADComs: false,
        wadcomsDescription: '',
        disableReverseShell: false,
        reverseShellDescription: '',
        disableOSINT: false,
        osintDescription: '',
        featuredMethodology: undefined,
        featuredToolkit: undefined
      };
    }
  },

  // Utility to remove undefined fields from an object
  removeUndefined(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
  },

  async updateSystemSettings(settings: SystemSettings): Promise<void> {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const settingsRef = doc(db, COLLECTIONS.SYSTEM, 'settings');
    // Remove undefined fields before saving
    const cleanedSettings = this.removeUndefined(settings);
    await setDoc(settingsRef, cleanedSettings, { merge: true });
  },

  // Hunting Toolkit Methods
  getHuntingToolkitGroups: async (): Promise<HuntingToolkitGroup[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning empty hunting toolkit groups");
      return [];
    }

    try {
      const huntingToolkitQuery = query(
        collection(db, COLLECTIONS.HUNTING_TOOLKIT),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(huntingToolkitQuery);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          description: data.description || '',
          sections: data.sections || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      });
    } catch (error) {
      console.error("Failed to get hunting toolkit groups:", error);
      return [];
    }
  },

  getHuntingToolkitGroupById: async (groupId: string): Promise<HuntingToolkitGroup | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot get hunting toolkit group");
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.HUNTING_TOOLKIT, groupId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || '',
          description: data.description || '',
          sections: data.sections || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      } else {
        console.warn(`Hunting toolkit group with ID ${groupId} not found`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to get hunting toolkit group with ID ${groupId}:`, error);
      return null;
    }
  },

  createHuntingToolkitGroup: async (group: Omit<HuntingToolkitGroup, 'id'>): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot create hunting toolkit group");
      return null;
    }

    try {
      const { title, description, sections } = group;
      
      const groupData = {
        title: title || '',
        description: description || '',
        sections: sections || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.HUNTING_TOOLKIT), groupData);
      return docRef.id;
    } catch (error) {
      console.error("Failed to create hunting toolkit group:", error);
      return null;
    }
  },

  saveHuntingToolkitGroup: async (group: HuntingToolkitGroup): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot save hunting toolkit group");
      return false;
    }

    try {
      const { id, title, description, sections } = group;
      
      const groupData = {
        title: title || '',
        description: description || '',
        sections: sections || [],
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, COLLECTIONS.HUNTING_TOOLKIT, id), groupData, { merge: true });
      return true;
    } catch (error) {
      console.error("Failed to save hunting toolkit group:", error);
      return false;
    }
  },

  deleteHuntingToolkitGroup: async (groupId: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot delete hunting toolkit group");
      return false;
    }

    try {
      await deleteDoc(doc(db, COLLECTIONS.HUNTING_TOOLKIT, groupId));
      return true;
    } catch (error) {
      console.error("Failed to delete hunting toolkit group:", error);
      return false;
    }
  },

  addSectionToGroup: async (groupId: string, section: Omit<HuntingToolkitSection, 'id'>): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot add section to hunting toolkit group");
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.HUNTING_TOOLKIT, groupId);
      
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Hunting toolkit group with ID ${groupId} not found`);
        }
        
        const data = docSnap.data();
        const sections = data.sections || [];
        
        const newSection: HuntingToolkitSection = {
          id: `section-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          title: section.title,
          description: section.description,
          command: section.command,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        sections.push(newSection);
        
        transaction.update(docRef, {
          sections,
          updatedAt: serverTimestamp()
        });
      });
      
      return 'success';
    } catch (error) {
      console.error("Failed to add section to hunting toolkit group:", error);
      return null;
    }
  },

  updateSectionInGroup: async (
    groupId: string,
    sectionId: string,
    updates: Partial<HuntingToolkitSection>
  ): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot update section in hunting toolkit group");
      return false;
    }

    try {
      const docRef = doc(db, COLLECTIONS.HUNTING_TOOLKIT, groupId);
      
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Hunting toolkit group with ID ${groupId} not found`);
        }
        
        const data = docSnap.data();
        const sections = data.sections || [];
        
        const updatedSections = sections.map((section: HuntingToolkitSection) => 
          section.id === sectionId 
            ? { ...section, ...updates, updatedAt: new Date().toISOString() }
            : section
        );
        
        transaction.update(docRef, {
          sections: updatedSections,
          updatedAt: serverTimestamp()
        });
      });
      
      return true;
    } catch (error) {
      console.error("Failed to update section in hunting toolkit group:", error);
      return false;
    }
  },

  deleteSectionFromGroup: async (groupId: string, sectionId: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot delete section from hunting toolkit group");
      return false;
    }

    try {
      const docRef = doc(db, COLLECTIONS.HUNTING_TOOLKIT, groupId);
      
      await runTransaction(db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Hunting toolkit group with ID ${groupId} not found`);
        }
        
        const data = docSnap.data();
        const sections = data.sections || [];
        
        const updatedSections = sections.filter((section: HuntingToolkitSection) => section.id !== sectionId);
        
        transaction.update(docRef, {
          sections: updatedSections,
          updatedAt: serverTimestamp()
        });
      });
      
      return true;
    } catch (error) {
      console.error("Failed to delete section from hunting toolkit group:", error);
      return false;
    }
  },

  // LOLBAS Settings Management
  getLOLBASSettings: async (): Promise<{ enabled: boolean; description: string }> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning default LOLBAS settings");
      return { enabled: true, description: "Access Living Off The Land Binaries and Scripts for Windows security testing" };
    }

    try {
      const settingsDoc = await getDoc(doc(db, COLLECTIONS.SYSTEM, 'settings'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        return {
          enabled: !(data.disableLOLBAS || false),
          description: data.lolbasDescription || "Access Living Off The Land Binaries and Scripts for Windows security testing"
        };
      }
      return { enabled: true, description: "Access Living Off The Land Binaries and Scripts for Windows security testing" };
    } catch (error) {
      console.error("Failed to get LOLBAS settings:", error);
      return { enabled: true, description: "Access Living Off The Land Binaries and Scripts for Windows security testing" };
    }
  },

  updateLOLBASSettings: async (settings: { enabled: boolean; description: string }): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot update LOLBAS settings");
      return false;
    }

    try {
      const settingsRef = doc(db, COLLECTIONS.SYSTEM, 'settings');
      await updateDoc(settingsRef, {
        disableLOLBAS: !settings.enabled,
        lolbasDescription: settings.description,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error("Failed to update LOLBAS settings:", error);
      return false;
    }
  },

  testLOLBASAPI: async (): Promise<{ success: boolean; entryCount?: number; error?: string }> => {
    try {
      const response = await fetch('/api/lolbas-proxy');
      if (response.ok) {
        const data = await response.json();
        return { success: true, entryCount: data.length };
      } else {
        return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
      }
    } catch (error) {
      console.error("LOLBAS API test failed:", error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  // Get LOLBAS data
  getLOLBASData: async (): Promise<LOLBASEntry[]> => {
    try {
      const response = await fetch('/api/lolbas-proxy');
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Failed to fetch LOLBAS data:", error);
      throw error;
    }
  }
};

// Add new Note interface after GuidedMethodology interface
export interface Note {
  id: string;
  title: string;
  content: string;
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
}

// Add the notes service at the end of the file, before the final closing brace
export const notesService = {
  // Get all notes
  getNotes: async (): Promise<Note[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning empty notes");
      return [];
    }

    try {
      const notesQuery = collection(db, COLLECTIONS.ADMIN_NOTES);
      const querySnapshot = query(
        notesQuery,
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(querySnapshot);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          content: data.content || '',
          tags: data.tags || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      });
    } catch (error) {
      console.error("Failed to get notes:", error);
      return [];
    }
  },

  // Get note by ID
  getNoteById: async (id: string): Promise<Note | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot get note");
      return null;
    }

    try {
      const docRef = doc(db, COLLECTIONS.ADMIN_NOTES, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || '',
          content: data.content || '',
          tags: data.tags || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      } else {
        console.warn(`Note with ID ${id} not found`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to get note with ID ${id}:`, error);
      return null;
    }
  },

  // Create a new note
  createNote: async (note: Omit<Note, 'id'>): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot create note");
      return null;
    }

    try {
      const { title, content, tags } = note;
      
      const noteData = {
        title: title || '',
        content: content || '',
        tags: tags || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.ADMIN_NOTES), noteData);
      return docRef.id;
    } catch (error) {
      console.error("Failed to create note:", error);
      return null;
    }
  },

  // Update an existing note
  updateNote: async (note: Note): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot update note");
      return false;
    }

    try {
      const { id, title, content, tags } = note;
      
      const noteData = {
        title: title || '',
        content: content || '',
        tags: tags || [],
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, COLLECTIONS.ADMIN_NOTES, id), noteData);
      return true;
    } catch (error) {
      console.error("Failed to update note:", error);
      return false;
    }
  },

  // Delete a note
  deleteNote: async (id: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, cannot delete note");
      return false;
    }

    try {
      await deleteDoc(doc(db, COLLECTIONS.ADMIN_NOTES, id));
      return true;
    } catch (error) {
      console.error(`Failed to delete note ${id}:`, error);
      return false;
    }
  },

  // Search notes by title or tags
  searchNotes: async (query: string): Promise<Note[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn("Firebase not initialized, returning empty notes");
      return [];
    }

    try {
      const notesCollection = collection(db, COLLECTIONS.ADMIN_NOTES);
      const querySnapshot = query(
        notesCollection,
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(querySnapshot);
      const allNotes = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          content: data.content || '',
          tags: data.tags || [],
          createdAt: normalizeTimestamp(data.createdAt),
          updatedAt: normalizeTimestamp(data.updatedAt)
        };
      });
      
      // Filter notes by search query (case insensitive)
      const searchLower = query.toLowerCase();
      return allNotes.filter(note => 
        note.title.toLowerCase().includes(searchLower) || 
        note.tags.some((tag: string) => tag.toLowerCase().includes(searchLower)) ||
        note.content.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      console.error("Failed to search notes:", error);
      return [];
    }
  }
}; 

// Hunting Toolkit interfaces
export interface HuntingToolkitSection {
  id: string;
  title: string;
  description: string;
  command: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface HuntingToolkitGroup {
  id: string;
  title: string;
  description?: string;
  sections: HuntingToolkitSection[];
  createdAt?: string;
  updatedAt?: string;
}

export interface HuntingToolkitWithTimestamps extends Omit<HuntingToolkitGroup, 'createdAt' | 'updatedAt'> {
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
} 

// LOLBAS interfaces
export interface LOLBASCommand {
  Command: string;
  Description: string;
  Usecase: string;
  Category: string;
  Privileges: string;
  MitreID: string;
  OperatingSystem: string;
  Tags: Array<{ [key: string]: string }>;
}

export interface LOLBASEntry {
  Name: string;
  Description: string;
  Author: string;
  Created: string;
  Commands: LOLBASCommand[];
  Full_Path: Array<{ Path: string }>;
  Detection: Array<{ Sigma?: string; BlockRule?: string; IOC?: string }>;
  Resources: Array<{ Link: string }>;
  url: string;
}

export interface Contribution {
  id: string;
  type: 'methodology' | 'guide' | 'toolkit' | 'data' | 'bug' | 'feature' | 'other';
  details: string;
  contributorName?: string;
  linkedin?: string;
  twitter?: string;
  reddit?: string;
  avatar?: string; // URL to uploaded avatar image
  status: 'pending' | 'accepted' | 'rejected';
  createdAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

export interface Contributor {
  id: string;
  name?: string;
  linkedin?: string;
  twitter?: string;
  reddit?: string;
  avatar?: string; // URL to uploaded avatar image
  contributionType: string;
  dateAccepted?: string;
}

export const contributionService = {
  // Submit a new contribution (status: pending)
  submitContribution: async (contribution: Omit<Contribution, 'id' | 'status' | 'createdAt'>) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = await addDoc(collection(db, COLLECTIONS.CONTRIBUTIONS), {
      ...contribution,
      status: 'pending',
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  },

  // Get all pending contributions
  getPendingContributions: async (): Promise<Contribution[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.error('Firebase not initialized:', { isFirebaseInitialized: firebaseService.isInitialized, db });
      return [];
    }
    try {
      const q = query(collection(db, COLLECTIONS.CONTRIBUTIONS), where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contribution));
    } catch (err) {
      console.error('Error loading pending contributions:', err);
      throw err;
    }
  },

  // Accept a contribution (update status, add to contributors)
  acceptContribution: async (contributionId: string, reviewer: string) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = doc(db, COLLECTIONS.CONTRIBUTIONS, contributionId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Contribution not found');
    const data = docSnap.data() as Contribution;
    await updateDoc(docRef, { status: 'accepted', reviewedAt: serverTimestamp(), reviewedBy: reviewer });
    // Add to contributors
    await addDoc(collection(db, COLLECTIONS.CONTRIBUTORS), {
      name: data.contributorName,
      linkedin: data.linkedin,
      twitter: data.twitter,
      reddit: data.reddit,
      avatar: data.avatar,
      contributionType: data.type,
      dateAccepted: serverTimestamp(),
    });
  },

  // Reject a contribution (update status)
  rejectContribution: async (contributionId: string, reviewer: string) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = doc(db, COLLECTIONS.CONTRIBUTIONS, contributionId);
    await updateDoc(docRef, { status: 'rejected', reviewedAt: serverTimestamp(), reviewedBy: reviewer });
  },

  // Get all contributors (for the contributors page)
  getContributors: async (): Promise<Contributor[]> => {
    if (!firebaseService.isInitialized || !db) return [];
    const q = query(collection(db, COLLECTIONS.CONTRIBUTORS), orderBy('dateAccepted', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contributor));
  },

  // Add a contributor manually (admin)
  addContributor: async (contributor: Omit<Contributor, 'id' | 'dateAccepted'>) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = await addDoc(collection(db, COLLECTIONS.CONTRIBUTORS), {
      ...contributor,
      dateAccepted: serverTimestamp(),
    });
    return docRef.id;
  },

  // Update a contributor (admin)
  updateContributor: async (id: string, updates: Partial<Contributor>) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = doc(db, COLLECTIONS.CONTRIBUTORS, id);
    await updateDoc(docRef, updates);
  },

  // Delete a contributor (admin)
  deleteContributor: async (id: string) => {
    if (!firebaseService.isInitialized || !db) throw new Error('Firebase not initialized');
    const docRef = doc(db, COLLECTIONS.CONTRIBUTORS, id);
    await deleteDoc(docRef);
  },

  // Get recent contributors (for landing page, e.g., last 5)
  getRecentContributors: async (limitCount = 5): Promise<Contributor[]> => {
    if (!firebaseService.isInitialized || !db) return [];
    const q = query(collection(db, COLLECTIONS.CONTRIBUTORS), orderBy('dateAccepted', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contributor));
  },
};

// Neural Network Writeup Learning System Types
export interface WriteupEntry {
  id: string;
  title: string;
  content: string;
  category: 'vulnerability' | 'technique' | 'tool' | 'methodology' | 'case-study';
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  author: string;
  createdAt: Date;
  updatedAt: Date;
  learningScore: number; // How well the AI learned from this writeup
  usageCount: number; // How many times this writeup was referenced
  isActive: boolean;
}

export interface NeuralPathway {
  id: string;
  query: string;
  context: {
    userStage: string;
    userTarget: string;
    userFindings: string;
    extraContext: string;
  };
  generatedPathway: {
    steps: Array<{
      step: number;
      title: string;
      description: string;
      tools: string[];
      commands: string[];
      reasoning: string;
      confidence: number;
      referencedWriteups: string[]; // IDs of writeups used
    }>;
    overallConfidence: number;
    learningInsights: string[];
  };
  feedback: {
    userRating?: number;
    userFeedback?: string;
    wasHelpful: boolean;
    improvements: string[];
  };
  createdAt: Date;
  modelVersion: string;
}

export interface NeuralPathwayConversationEntry {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pathwayId?: string;
  timestamp: string;
}

export interface NeuralPathwaySession {
  id: string;
  conversation: NeuralPathwayConversationEntry[];
  lastPathway: NeuralPathway | null;
  pathwayHistory: NeuralPathway[];
  createdAt: Date;
  updatedAt: Date;
}

const parseDateField = (value: any): Date => {
  if (!value) {
    return new Date();
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  if (value.toDate && typeof value.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date ? converted : new Date();
  }

  return new Date();
};

const sanitizePathwayForStorage = (pathway: NeuralPathway | null) => {
  if (!pathway) return null;

  const createdAt =
    pathway.createdAt instanceof Date
      ? pathway.createdAt.toISOString()
      : typeof pathway.createdAt === 'string'
        ? pathway.createdAt
        : new Date().toISOString();

  const convertStep = (step: NeuralPathway['generatedPathway']['steps'][number], index: number) => ({
    step: typeof step.step === 'number' ? step.step : index + 1,
    title: step.title || `Step ${index + 1}`,
    description: step.description || '',
    tools: Array.isArray(step.tools) ? step.tools.filter(Boolean) : [],
    commands: Array.isArray(step.commands) ? step.commands.filter(Boolean) : [],
    reasoning: step.reasoning || '',
    confidence: typeof step.confidence === 'number' ? step.confidence : 0,
    referencedWriteups: Array.isArray(step.referencedWriteups)
      ? step.referencedWriteups.filter(Boolean)
      : []
  });

  return {
    id: pathway.id || `pathway-${Date.now()}`,
    query: pathway.query || '',
    context: {
      userStage: pathway.context?.userStage || '',
      userTarget: pathway.context?.userTarget || '',
      userFindings: pathway.context?.userFindings || '',
      extraContext: pathway.context?.extraContext || ''
    },
    generatedPathway: {
      steps: Array.isArray(pathway.generatedPathway?.steps)
        ? pathway.generatedPathway.steps.map(convertStep)
        : [],
      overallConfidence:
        typeof pathway.generatedPathway?.overallConfidence === 'number'
          ? pathway.generatedPathway.overallConfidence
          : 0,
      learningInsights: Array.isArray(pathway.generatedPathway?.learningInsights)
        ? pathway.generatedPathway.learningInsights.filter(Boolean)
        : []
    },
    feedback: {
      wasHelpful: pathway.feedback?.wasHelpful ?? false,
      improvements: Array.isArray(pathway.feedback?.improvements)
        ? pathway.feedback.improvements.filter(Boolean)
        : [],
      userRating: pathway.feedback?.userRating ?? null,
      userFeedback: pathway.feedback?.userFeedback ?? null
    },
    createdAt,
    modelVersion: pathway.modelVersion || '1.0.0'
  };
};

const restorePathwayFromStorage = (stored: any): NeuralPathway | null => {
  if (!stored) return null;
  try {
    const steps = Array.isArray(stored.generatedPathway?.steps)
      ? stored.generatedPathway.steps.map((step: any, index: number) => ({
          step: typeof step.step === 'number' ? step.step : index + 1,
          title: step.title || `Step ${index + 1}`,
          description: step.description || '',
          tools: Array.isArray(step.tools) ? step.tools : [],
          commands: Array.isArray(step.commands) ? step.commands : [],
          reasoning: step.reasoning || '',
          confidence: typeof step.confidence === 'number' ? step.confidence : 0.5,
          referencedWriteups: Array.isArray(step.referencedWriteups) ? step.referencedWriteups : []
        }))
      : [];

    return {
      id: stored.id,
      query: stored.query || '',
      context: {
        userStage: stored.context?.userStage || '',
        userTarget: stored.context?.userTarget || '',
        userFindings: stored.context?.userFindings || '',
        extraContext: stored.context?.extraContext || ''
      },
      generatedPathway: {
        steps,
        overallConfidence: typeof stored.generatedPathway?.overallConfidence === 'number'
          ? stored.generatedPathway.overallConfidence
          : (steps.reduce((sum, step) => sum + step.confidence, 0) / Math.max(steps.length, 1)) || 0,
        learningInsights: Array.isArray(stored.generatedPathway?.learningInsights)
          ? stored.generatedPathway.learningInsights
          : []
      },
      feedback: {
        wasHelpful: stored.feedback?.wasHelpful ?? false,
        improvements: Array.isArray(stored.feedback?.improvements) ? stored.feedback.improvements : [],
        userRating: stored.feedback?.userRating,
        userFeedback: stored.feedback?.userFeedback
      },
      createdAt: parseDateField(stored.createdAt),
      modelVersion: stored.modelVersion || '1.0.0'
    } as NeuralPathway;
  } catch (error) {
    console.error('Failed to restore pathway from storage:', error);
    return null;
  }
};

export interface LearningAnalytics {
  totalWriteups: number;
  totalPathways: number;
  averageConfidence: number;
  topPerformingWriteups: WriteupEntry[];
  learningProgress: {
    date: string;
    confidence: number;
    accuracy: number;
  }[];
  categoryPerformance: {
    category: string;
    averageScore: number;
    usageCount: number;
  }[];
}

// Neural Network Service
export const neuralNetworkService = {
  // Writeup Management
  async addWriteup(writeup: Omit<WriteupEntry, 'id' | 'createdAt' | 'updatedAt' | 'learningScore' | 'usageCount'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'writeups'), {
      ...writeup,
      createdAt: new Date(),
      updatedAt: new Date(),
      learningScore: 0,
      usageCount: 0,
      isActive: true
    });
    return docRef.id;
  },

  async updateWriteup(id: string, updates: Partial<WriteupEntry>): Promise<void> {
    await updateDoc(doc(db, 'writeups', id), {
      ...updates,
      updatedAt: new Date()
    });
  },

  async getWriteups(filters?: {
    category?: string;
    difficulty?: string;
    tags?: string[];
    isActive?: boolean;
  }): Promise<WriteupEntry[]> {
    let q = collection(db, 'writeups');
    
    if (filters?.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters?.difficulty) {
      q = query(q, where('difficulty', '==', filters.difficulty));
    }
    if (filters?.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      
      // Handle different date formats safely
      const parseDate = (dateField: any): Date => {
        if (!dateField) return new Date();
        
        if (typeof dateField === 'string') {
          return new Date(dateField);
        } else if (dateField instanceof Date) {
          return dateField;
        } else if (dateField.toDate && typeof dateField.toDate === 'function') {
          return dateField.toDate();
        } else {
          return new Date();
        }
      };
      
      return {
        id: doc.id,
        ...data,
        createdAt: parseDate(data.createdAt),
        updatedAt: parseDate(data.updatedAt)
      } as WriteupEntry;
    });
  },

  async deleteWriteup(id: string): Promise<void> {
    await deleteDoc(doc(db, 'writeups', id));
  },

  // Neural Pathway Generation
  async generateNeuralPathway(context: {
    userStage: string;
    userTarget: string;
    userFindings: string;
    extraContext: string;
  }): Promise<NeuralPathway> {
    try {
      // Get relevant writeups for learning
      const relevantWriteups = await this.getRelevantWriteups(context);
      
      // Generate pathway using neural network approach
      const response = await fetch('/api/methodology/pathway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context,
          writeups: relevantWriteups,
          modelVersion: '1.0.0'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate neural pathway');
      }

      const result = await response.json();
      
      // Save the generated pathway
      const pathway: Omit<NeuralPathway, 'id'> = {
        query: `${context.userStage} - ${context.userTarget}`,
        context,
        generatedPathway: result.pathway,
        feedback: { wasHelpful: false, improvements: [] },
        createdAt: new Date(),
        modelVersion: '1.0.0'
      };

      // Sanitize before saving to Firestore
      const sanitized = JSON.parse(JSON.stringify(pathway));
      const docRef = await addDoc(collection(db, 'neural-pathways'), sanitized);
      
      // Update writeup usage counts
      await this.updateWriteupUsage(result.pathway.steps.flatMap(step => step.referencedWriteups));
      
      return { id: docRef.id, ...pathway };
    } catch (error) {
      console.error('Neural pathway generation error:', error);
      throw error;
    }
  },

  async getRelevantWriteups(context: {
    userStage: string;
    userTarget: string;
    userFindings: string;
    extraContext: string;
  }): Promise<WriteupEntry[]> {
    // Extract keywords from context
    const keywords = this.extractKeywords(context);
    
    // Get writeups that match keywords
    const allWriteups = await this.getWriteups({ isActive: true });
    
    // Score writeups based on relevance
    const scoredWriteups = allWriteups.map(writeup => ({
      ...writeup,
      relevanceScore: this.calculateRelevanceScore(writeup, keywords)
    }));

    // Return top relevant writeups
    return scoredWriteups
      .sort((a, b) => (b as any).relevanceScore - (a as any).relevanceScore)
      .slice(0, 10)
      .map(({ relevanceScore, ...writeup }) => writeup);
  },

  extractKeywords(context: any): string[] {
    const text = `${context.userStage} ${context.userTarget} ${context.userFindings} ${context.extraContext}`.toLowerCase();
    const keywords = text.match(/\b\w+\b/g) || [];
    return [...new Set(keywords)].filter(word => word.length > 3);
  },

  calculateRelevanceScore(writeup: WriteupEntry, keywords: string[]): number {
    const writeupText = `${writeup.title} ${writeup.content} ${writeup.tags.join(' ')}`.toLowerCase();
    let score = 0;
    
    keywords.forEach(keyword => {
      const regex = new RegExp(keyword, 'gi');
      const matches = writeupText.match(regex);
      if (matches) {
        score += matches.length;
      }
    });
    
    // Boost score based on learning performance
    score += writeup.learningScore * 0.5;
    
    return score;
  },

  async updateWriteupUsage(writeupIds: string[]): Promise<void> {
    if (!writeupIds || writeupIds.length === 0) return;
    
    try {
      const batch = writeBatch(db);
      
      writeupIds.forEach(id => {
        const docRef = doc(db, 'writeups', id);
        batch.update(docRef, {
          usageCount: increment(1)
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Failed to update writeup usage counts:', error);
      // Don't throw the error to prevent breaking the pathway generation
    }
  },

  // Feedback and Learning
  async submitPathwayFeedback(pathwayId: string, feedback: {
    rating?: number;
    feedback?: string;
    wasHelpful: boolean;
    improvements: string[];
  }): Promise<void> {
    try {
      await updateDoc(doc(db, 'neural-pathways', pathwayId), {
        feedback,
        'generatedPathway.overallConfidence': increment(feedback.wasHelpful ? 0.1 : -0.1)
      });

      // Update learning scores for referenced writeups
      const pathway = await getDoc(doc(db, 'neural-pathways', pathwayId));
      if (pathway.exists()) {
        const data = pathway.data() as NeuralPathway;
        const writeupIds = data.generatedPathway.steps.flatMap(step => step.referencedWriteups);
        
        if (writeupIds && writeupIds.length > 0) {
          try {
            const batch = writeBatch(db);
            writeupIds.forEach(id => {
              const docRef = doc(db, 'writeups', id);
              batch.update(docRef, {
                learningScore: increment(feedback.wasHelpful ? 0.1 : -0.1)
              });
            });
            await batch.commit();
          } catch (error) {
            console.error('Failed to update writeup learning scores:', error);
            // Don't throw the error to prevent breaking the feedback submission
          }
        }
      }
    } catch (error) {
      console.error('Failed to submit pathway feedback:', error);
      throw error;
    }
  },

  // Analytics
  async getLearningAnalytics(): Promise<LearningAnalytics> {
    const [writeups, pathways] = await Promise.all([
      this.getWriteups(),
      this.getNeuralPathways()
    ]);

    const topWriteups = writeups
      .sort((a, b) => b.learningScore - a.learningScore)
      .slice(0, 5);

    const averageConfidence = pathways.length > 0 
      ? pathways.reduce((sum, p) => sum + p.generatedPathway.overallConfidence, 0) / pathways.length
      : 0;

    const categoryPerformance = this.calculateCategoryPerformance(writeups);

    return {
      totalWriteups: writeups.length,
      totalPathways: pathways.length,
      averageConfidence,
      topPerformingWriteups: topWriteups,
      learningProgress: this.calculateLearningProgress(pathways),
      categoryPerformance
    };
  },

  async getNeuralPathways(): Promise<NeuralPathway[]> {
    const snapshot = await getDocs(collection(db, 'neural-pathways'));
    return snapshot.docs.map(doc => {
      const data = doc.data();
      let createdAt: Date;
      
      // Handle different date formats safely
      if (data.createdAt) {
        if (typeof data.createdAt === 'string') {
          createdAt = new Date(data.createdAt);
        } else if (data.createdAt instanceof Date) {
          createdAt = data.createdAt;
        } else if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
          createdAt = data.createdAt.toDate();
        } else {
          // Fallback to current date if we can't parse the date
          createdAt = new Date();
        }
      } else {
        // Fallback to current date if no createdAt
        createdAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt
      } as NeuralPathway;
    });
  },

  async getNeuralPathwayById(id: string): Promise<NeuralPathway | null> {
    try {
      const pathwayDoc = await getDoc(doc(db, 'neural-pathways', id));
      if (!pathwayDoc.exists()) {
        return null;
      }
      const data = pathwayDoc.data();
      return restorePathwayFromStorage({ id: pathwayDoc.id, ...data });
    } catch (error) {
      console.error(`Failed to fetch neural pathway ${id}:`, error);
      return null;
    }
  },

  async startPathwaySession(): Promise<string> {
    try {
      if (!firebaseService.isInitialized) {
        const fallbackId = `offline-session-${Date.now()}`;
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem(
            fallbackId,
            JSON.stringify({
              conversation: [],
              pathwayHistory: [],
              lastPathway: null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            })
          );
        }
        return fallbackId;
      }

      const sessionRef = await addDoc(collection(db, COLLECTIONS.NEURAL_PATHWAY_SESSIONS), {
        userId: auth?.currentUser?.uid || null,
        conversation: [],
        pathwayHistory: [],
        lastPathway: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return sessionRef.id;
    } catch (error) {
      console.error('Failed to start neural pathway session:', error);
      throw error;
    }
  },

  async getPathwaySession(sessionId: string): Promise<NeuralPathwaySession | null> {
    try {
      if (!firebaseService.isInitialized) {
        if (typeof window === 'undefined') return null;
        const stored = window.sessionStorage?.getItem(sessionId);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        return {
          id: sessionId,
          conversation: Array.isArray(parsed.conversation) ? parsed.conversation : [],
          pathwayHistory: Array.isArray(parsed.pathwayHistory)
            ? parsed.pathwayHistory.map((item: any) => restorePathwayFromStorage(item)).filter(Boolean) as NeuralPathway[]
            : [],
          lastPathway: restorePathwayFromStorage(parsed.lastPathway),
          createdAt: parseDateField(parsed.createdAt),
          updatedAt: parseDateField(parsed.updatedAt)
        };
      }

      const sessionDoc = await getDoc(doc(db, COLLECTIONS.NEURAL_PATHWAY_SESSIONS, sessionId));
      if (!sessionDoc.exists()) {
        return null;
      }

      const data = sessionDoc.data();
      const rawHistory = Array.isArray(data.pathwayHistory) ? data.pathwayHistory : [];

      return {
        id: sessionDoc.id,
        conversation: Array.isArray(data.conversation) ? data.conversation : [],
        pathwayHistory: rawHistory
          .map(item => restorePathwayFromStorage(item))
          .filter(Boolean) as NeuralPathway[],
        lastPathway: restorePathwayFromStorage(data.lastPathway),
        createdAt: parseDateField(data.createdAt),
        updatedAt: parseDateField(data.updatedAt)
      };
    } catch (error) {
      console.error(`Failed to retrieve neural pathway session ${sessionId}:`, error);
      return null;
    }
  },

  async updatePathwaySession(sessionId: string, data: {
    conversation?: NeuralPathwayConversationEntry[];
    pathwayHistory?: NeuralPathway[];
    lastPathway?: NeuralPathway | null;
  }): Promise<void> {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required to update pathway session');
      }

      if (!firebaseService.isInitialized) {
        if (typeof window !== 'undefined') {
          const existing = window.sessionStorage?.getItem(sessionId);
          const parsed = existing ? JSON.parse(existing) : {};
          const sanitizedConversation = data.conversation
            ? data.conversation.map((entry, index) => ({
                id: entry.id || `entry-${Date.now()}-${index}`,
                role: entry.role === 'assistant' ? 'assistant' : 'user',
                content: entry.content ?? '',
                pathwayId: entry.pathwayId ?? null,
                timestamp: entry.timestamp ?? new Date().toISOString()
              }))
            : parsed.conversation || [];
          const payload = {
            ...parsed,
            conversation: sanitizedConversation,
            pathwayHistory: data.pathwayHistory
              ? data.pathwayHistory.map(pathway => sanitizePathwayForStorage(pathway)).filter(Boolean)
              : parsed.pathwayHistory || [],
            lastPathway: data.lastPathway ? sanitizePathwayForStorage(data.lastPathway) : parsed.lastPathway || null,
            updatedAt: new Date().toISOString(),
            createdAt: parsed.createdAt || new Date().toISOString()
          };
          window.sessionStorage?.setItem(sessionId, JSON.stringify(payload));
        }
        return;
      }

      const sessionRef = doc(db, COLLECTIONS.NEURAL_PATHWAY_SESSIONS, sessionId);
      const updatePayload: Record<string, any> = {
        updatedAt: serverTimestamp()
      };

      if (data.conversation) {
        updatePayload.conversation = data.conversation.map(entry => ({
          id: entry.id || `entry-${Date.now()}`,
          role: entry.role === 'assistant' ? 'assistant' : 'user',
          content: entry.content ?? '',
          pathwayId: entry.pathwayId ?? null,
          timestamp: entry.timestamp ?? new Date().toISOString()
        }));
      }

      if (data.pathwayHistory) {
        updatePayload.pathwayHistory = data.pathwayHistory
          .map(pathway => sanitizePathwayForStorage(pathway))
          .filter(Boolean);
      }

      if (data.lastPathway !== undefined) {
        updatePayload.lastPathway = sanitizePathwayForStorage(data.lastPathway ?? null);
      }

      await setDoc(sessionRef, updatePayload, { merge: true });
    } catch (error) {
      console.error(`Failed to update neural pathway session ${sessionId}:`, error);
      throw error;
    }
  },

  async clearPathwaySession(sessionId: string): Promise<void> {
    try {
      if (!firebaseService.isInitialized) {
        if (typeof window !== 'undefined') {
          window.sessionStorage?.removeItem(sessionId);
        }
        return;
      }

      await updateDoc(doc(db, COLLECTIONS.NEURAL_PATHWAY_SESSIONS, sessionId), {
        conversation: [],
        pathwayHistory: [],
        lastPathway: null,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error(`Failed to clear neural pathway session ${sessionId}:`, error);
    }
  },

  calculateCategoryPerformance(writeups: WriteupEntry[]) {
    const categories = ['vulnerability', 'technique', 'tool', 'methodology', 'case-study'];
    return categories.map(category => {
      const categoryWriteups = writeups.filter(w => w.category === category);
      const averageScore = categoryWriteups.length > 0
        ? categoryWriteups.reduce((sum, w) => sum + w.learningScore, 0) / categoryWriteups.length
        : 0;
      
      return {
        category,
        averageScore,
        usageCount: categoryWriteups.reduce((sum, w) => sum + w.usageCount, 0)
      };
    });
  },

  calculateLearningProgress(pathways: NeuralPathway[]) {
    // Group by date and calculate average confidence
    const dailyData = pathways.reduce((acc, pathway) => {
      let date: string;
      
      // Handle different date formats safely
      if (pathway.createdAt) {
        if (typeof pathway.createdAt === 'string') {
          date = new Date(pathway.createdAt).toISOString().split('T')[0];
        } else if (pathway.createdAt instanceof Date) {
          date = pathway.createdAt.toISOString().split('T')[0];
        } else if (pathway.createdAt.toDate && typeof pathway.createdAt.toDate === 'function') {
          date = pathway.createdAt.toDate().toISOString().split('T')[0];
        } else {
          // Fallback to current date if we can't parse the date
          date = new Date().toISOString().split('T')[0];
        }
      } else {
        // Fallback to current date if no createdAt
        date = new Date().toISOString().split('T')[0];
      }
      
      if (!acc[date]) {
        acc[date] = { confidence: 0, count: 0 };
      }
      acc[date].confidence += pathway.generatedPathway.overallConfidence;
      acc[date].count += 1;
      return acc;
    }, {} as Record<string, { confidence: number; count: number }>);

    return Object.entries(dailyData).map(([date, data]) => ({
      date,
      confidence: data.confidence / data.count,
      accuracy: data.confidence / data.count // Simplified for now
    }));
  },

  // New function to get all pathway feedback for admin viewing
  async getAllPathwayFeedback(): Promise<NeuralPathwayFeedback[]> {
    const snapshot = await getDocs(collection(db, 'neural-pathways'));
    const feedbackData: NeuralPathwayFeedback[] = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data() as NeuralPathway;
      
      // Only include pathways that have feedback
      if (data.feedback && (
        data.feedback.userRating || 
        data.feedback.userFeedback || 
        (data.feedback.improvements && data.feedback.improvements.length > 0)
      )) {
        feedbackData.push({
          id: doc.id,
          pathwayId: doc.id,
          pathwayQuery: data.query || 'No query',
          pathwayContext: data.context || {
            userStage: '',
            userTarget: '',
            userFindings: '',
            extraContext: ''
          },
          rating: data.feedback.userRating || 0,
          wasHelpful: data.feedback.wasHelpful || false,
          feedback: data.feedback.userFeedback || '',
          improvements: data.feedback.improvements || [],
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          overallConfidence: data.generatedPathway?.overallConfidence || 0,
          stepsCount: data.generatedPathway?.steps?.length || 0
        });
      }
    });
    
    // Sort by creation date (newest first)
    return feedbackData.sort((a, b) => {
      try {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      } catch (error) {
        console.error('Error sorting feedback data by date:', error);
        return 0; // Keep original order if date parsing fails
      }
    });
  },
};

// New interface for admin feedback viewing
export interface NeuralPathwayFeedback {
  id: string;
  pathwayId: string;
  pathwayQuery: string;
  pathwayContext: {
    userStage: string;
    userTarget: string;
    userFindings: string;
    extraContext: string;
  };
  rating: number;
  wasHelpful: boolean;
  feedback: string;
  improvements: string[];
  createdAt: Date;
  overallConfidence: number;
  stepsCount: number;
}

// Add avatar upload function - FREE VERSION
export const uploadAvatarImage = async (file: File, contributorName: string): Promise<string> => {
  // Free alternative: Convert image to base64 data URL
  // This avoids Firebase Storage costs and CORS issues
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      const result = reader.result as string;
      if (result) {
        console.log('Avatar converted to base64 successfully');
        resolve(result);
      } else {
        reject(new Error('Failed to convert image to base64'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read image file'));
    };
    
    // Convert to base64 data URL
    reader.readAsDataURL(file);
  });
};

// Helper to ensure Firebase is initialized
export function requireFirebaseInitialized() {
  const instance = FirebaseService.getInstance();
  if (!instance.isInitialized) throw new Error('Firebase is not initialized');
  return instance;
}

/**
 * Clear all user session data from localStorage and cache
 * This should be called when a user signs out to prevent session persistence issues
 */
export const clearUserSessionData = async (userId?: string): Promise<void> => {
  try {
    // Clear all localStorage session data
    const sessionKeys = [
      'rawpa_user_name',
      'rawpa_device_id',
      'rawpa_target_domain',
      'rawpa_api_key',
      'rawpa_hf_api_key'
    ];
    
    // Clear session-related localStorage items
    sessionKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear any cached user data from Redis/localStorage
    if (userId) {
      try {
        await redisService.clearUserCache(userId);
      } catch (error) {
        console.error('Error clearing user cache:', error);
      }
    }
    
    console.log('User session data cleared successfully');
  } catch (error) {
    console.error('Error clearing user session data:', error);
  }
};